import * as React from "react";
import { Box, Button, IconButton, Slider } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { PerspectiveCamera, Vector3 } from "three";

import { ActiveRallyVideo } from "./ActiveRallyVideo";
import { CondensedRallyTimeline_ContextOnly } from "./CondensedRallyTimeline_ContextOnly";
import {
  ShotUniverse3DView,
  type ShotUniverseLabSelection,
  type ShotUniverseLabSelectionRestore,
  type ShotUniverseCameraAdjust,
  type ShotUniverseVideoCourtProjection,
  type ShotUniverseVisibility,
} from "./ShotUniverse3DView";
import { ShotBookmarksPopup } from "./ShotBookmarksPopup";
import type { FirefightClip } from "./GameStory_firefights";
import type {
  PlayerIndexMap,
  RenderPlayerIdentity,
} from "./GameStoryDialog_UPDATED";
import type { KitchenArrivalRow, ShotNumberOverlay } from "./RallyBoardPopup";
import type { ShotOpportunityLabContext } from "./GameStory_OpportunityFinderPopup";
import {
  buildShotUniverseContext,
  pointToThree,
  type ShotUniverseContext,
  type ShotUniverseShotFamily,
} from "./shotUniverseHelpers";
import type { CvJson } from "../../shared/types/cvJson";
import type { InsightsJson } from "../../shared/types/insightsEtcTypes";
import type { RallyReviewAgentResponse } from "../../shared/types/rallyReviewAgentTypes";
import {
  deleteShotBookmark,
  updateShotBookmarkDescription,
  upsertShotBookmark,
  useShotBookmarks,
  type ShotBookmark,
} from "./shotBookmarks";

type ViewMode = "video" | "court3d";
type RallyReviewPipeline = {
  mode: "candidate_prepass";
  candidateCount: number;
  contextBytes?: number;
  modelCallCount?: number;
  candidates: Array<{
    shotNumber: number;
    label?: string;
    score?: number;
    reasons: string[];
  }>;
};

function opportunityResultEvidenceLabel(context: ShotOpportunityLabContext) {
  if (context.teamResult === "unknown") return "unknown";
  return `${context.teamResult} ${context.shotEndedRally ? "on shot" : "later"}`;
}
type RallyReviewWithPipeline = RallyReviewAgentResponse & {
  reviewPipeline?: RallyReviewPipeline;
};

const DEFAULT_CAMERA_ADJUST: ShotUniverseCameraAdjust = {
  position: [0, 0, 0],
  target: [0, 0, 0],
  fov: 0,
  roll: 0,
  offset: [0, 0],
};
const TRAJECTORY_SWITCH_FRACTION = 0.5;
const SHOW_TIMING_DEBUG = false;
const SHOW_SHOT_SWITCH_TIMING_TUNER = false;
const LINGERING_SHOT_MAX_OPACITY = 1;
const LINGERING_SHOT_FADE_MS = 420;
const LINGERING_SHOT_MIN_SEC = 0.18;
const LINGERING_SHOT_MAX_SEC = 0.62;
const ENABLE_RALLY_REVIEW_AGENT = false;
type ShotSwitchTimingTuning = {
  fraction: number;
  holdCurrentFraction: number;
  previewNextFraction: number;
};
const DEFAULT_SHOT_SWITCH_TIMING: ShotSwitchTimingTuning = {
  fraction: 0.38,
  holdCurrentFraction: 0.04,
  previewNextFraction: 0.25,
};
const LEGACY_SHOT_SWITCH_TIMING: ShotSwitchTimingTuning = {
  fraction: TRAJECTORY_SWITCH_FRACTION,
  holdCurrentFraction: 0,
  previewNextFraction: 0,
};
const SHOT_UNIVERSE_VISIBILITY: Partial<ShotUniverseVisibility> = {
  actualOpacity: 1,
  actualDimOpacity: 0.82,
  actualTubeRadius: 0.11,
  ghostOpacity: 0.74,
  ghostBadOpacity: 0.46,
  ghostSelectedOpacity: 1,
  ghostDimOpacity: 0.42,
  shapeFanOpacityBase: 0.78,
  shapeFanOpacityStep: 0.04,
  targetOpacity: 0.94,
  targetDimOpacity: 0.72,
};
const SHOT_SPACE_VIEW_BLEED_PX = 220;

type CourtFitPointId =
  | "farBaselineLeft"
  | "farBaselineCenter"
  | "farBaselineRight"
  | "farKitchenLeft"
  | "farKitchenCenter"
  | "farKitchenRight"
  | "nearKitchenCenter"
  | "nearBaselineCenter"
  | "nearBaselineAnyLeft"
  | "nearBaselineAnyRight"
  | "nearLeftSidelineAnyNear"
  | "nearLeftSidelineAnyFar"
  | "nearRightSidelineAnyNear"
  | "nearRightSidelineAnyFar"
  | "nearKitchenLeft"
  | "nearKitchenRight"
  | "nearNetLeft"
  | "nearNetRight"
  | "nearBaselineLeft"
  | "nearBaselineRight";

type CourtFitLandmark = {
  id: CourtFitPointId;
  label: string;
  hint: string;
  world: Vector3;
};

type CourtFitClick = {
  pointId: CourtFitPointId;
  x: number;
  y: number;
};

type CourtFitResidual = {
  pointId: CourtFitPointId;
  label: string;
  clickX: number;
  clickY: number;
  fitX: number;
  fitY: number;
  errorPx: number;
  constraint: "point" | "line";
};

type CourtHomography = [number, number, number, number, number, number, number, number, number];

const COURT_FIT_LANDMARKS: CourtFitLandmark[] = [
  {
    id: "farBaselineLeft",
    label: "Far baseline left",
    hint: "far baseline/sideline corner on the left side of the court",
    world: new Vector3(-10, 0, -22),
  },
  {
    id: "farBaselineCenter",
    label: "Far baseline center",
    hint: "middle of the far baseline, where the service centerline meets it",
    world: new Vector3(0, 0, -22),
  },
  {
    id: "farBaselineRight",
    label: "Far baseline right",
    hint: "far baseline/sideline corner on the right side of the court",
    world: new Vector3(10, 0, -22),
  },
  {
    id: "farKitchenLeft",
    label: "Far kitchen left",
    hint: "left sideline where the far kitchen line crosses",
    world: new Vector3(-10, 0, -7),
  },
  {
    id: "farKitchenCenter",
    label: "Far kitchen center",
    hint: "centerline where the far kitchen line crosses",
    world: new Vector3(0, 0, -7),
  },
  {
    id: "farKitchenRight",
    label: "Far kitchen right",
    hint: "right sideline where the far kitchen line crosses",
    world: new Vector3(10, 0, -7),
  },
  {
    id: "nearBaselineCenter",
    label: "Near baseline center",
    hint: "middle of the near baseline, where the service centerline meets it",
    world: new Vector3(0, 0, 22),
  },
  {
    id: "nearBaselineAnyLeft",
    label: "Baseline point left",
    hint: "any visible point on the near baseline, left of center",
    world: new Vector3(-5, 0, 22),
  },
  {
    id: "nearBaselineAnyRight",
    label: "Baseline point right",
    hint: "any visible point on the near baseline, right of center",
    world: new Vector3(5, 0, 22),
  },
  {
    id: "nearLeftSidelineAnyNear",
    label: "Left sideline near point",
    hint: "any visible point on the near-left sideline, closer to the baseline",
    world: new Vector3(-10, 0, 16),
  },
  {
    id: "nearLeftSidelineAnyFar",
    label: "Left sideline far point",
    hint: "any visible point on the near-left sideline, closer to the net",
    world: new Vector3(-10, 0, 4),
  },
  {
    id: "nearRightSidelineAnyNear",
    label: "Right sideline near point",
    hint: "any visible point on the near-right sideline, closer to the baseline",
    world: new Vector3(10, 0, 16),
  },
  {
    id: "nearRightSidelineAnyFar",
    label: "Right sideline far point",
    hint: "any visible point on the near-right sideline, closer to the net",
    world: new Vector3(10, 0, 4),
  },
  {
    id: "nearKitchenLeft",
    label: "Near kitchen left",
    hint: "left sideline where the near kitchen line crosses",
    world: new Vector3(-10, 0, 7),
  },
  {
    id: "nearKitchenRight",
    label: "Near kitchen right",
    hint: "right sideline where the near kitchen line crosses",
    world: new Vector3(10, 0, 7),
  },
  {
    id: "nearKitchenCenter",
    label: "Near kitchen center",
    hint: "centerline where the near kitchen line crosses",
    world: new Vector3(0, 0, 7),
  },
  {
    id: "nearNetLeft",
    label: "Near net left",
    hint: "left sideline at the net plane",
    world: new Vector3(-10, 0, 0),
  },
  {
    id: "nearNetRight",
    label: "Near net right",
    hint: "right sideline at the net plane",
    world: new Vector3(10, 0, 0),
  },
  {
    id: "nearBaselineLeft",
    label: "Optional baseline left",
    hint: "bottom-left baseline/sideline corner, if visible",
    world: new Vector3(-10, 0, 22),
  },
  {
    id: "nearBaselineRight",
    label: "Optional baseline right",
    hint: "bottom-right baseline/sideline corner, if visible",
    world: new Vector3(10, 0, 22),
  },
];

const PBV_CV_COURT_POINT_IDS: CourtFitPointId[] = [
  "farBaselineLeft",
  "farBaselineCenter",
  "farBaselineRight",
  "farKitchenLeft",
  "farKitchenCenter",
  "farKitchenRight",
  "nearKitchenLeft",
  "nearKitchenCenter",
  "nearKitchenRight",
  "nearBaselineLeft",
  "nearBaselineCenter",
  "nearBaselineRight",
];

const COURT_DEBUG_POLYLINES: Array<Array<[number, number]>> = [
  [
    [-10, -22],
    [10, -22],
    [10, 22],
    [-10, 22],
    [-10, -22],
  ],
  [
    [-10, 0],
    [10, 0],
  ],
  [
    [-10, -7],
    [10, -7],
  ],
  [
    [-10, 7],
    [10, 7],
  ],
  [
    [0, -22],
    [0, -7],
  ],
  [
    [0, 7],
    [0, 22],
  ],
];
const COURT_DEBUG_POLYLINE_LABELS = [
  "outer court",
  "net",
  "far kitchen",
  "near kitchen",
  "far centerline",
  "near centerline",
];

function courtFitStorageKey(packageId: string): string {
  return `shotUniverse.courtFitClicks.${packageId}`;
}

function normalizeCourtFitClicks(value: unknown): CourtFitClick[] {
  if (!Array.isArray(value)) return [];
  const validIds = new Set(COURT_FIT_LANDMARKS.map((landmark) => landmark.id));
  const seen = new Set<CourtFitPointId>();
  const clicks: CourtFitClick[] = [];
  for (const item of value) {
    const pointId = item?.pointId as CourtFitPointId;
    const x = Number(item?.x);
    const y = Number(item?.y);
    if (!validIds.has(pointId) || !Number.isFinite(x) || !Number.isFinite(y)) continue;
    if (seen.has(pointId)) continue;
    seen.add(pointId);
    clicks.push({ pointId, x, y });
  }
  return clicks.sort(
    (a, b) =>
      COURT_FIT_LANDMARKS.findIndex((landmark) => landmark.id === a.pointId) -
      COURT_FIT_LANDMARKS.findIndex((landmark) => landmark.id === b.pointId),
  );
}

function loadCourtFitClicks(packageId: string): CourtFitClick[] {
  try {
    return normalizeCourtFitClicks(JSON.parse(localStorage.getItem(courtFitStorageKey(packageId)) ?? "[]"));
  } catch {
    return [];
  }
}

function saveCourtFitClicks(packageId: string, clicks: CourtFitClick[]) {
  try {
    localStorage.setItem(courtFitStorageKey(packageId), JSON.stringify(normalizeCourtFitClicks(clicks)));
  } catch {}
}

function clearCourtFitClicksStorage(packageId: string) {
  try {
    localStorage.removeItem(courtFitStorageKey(packageId));
  } catch {}
}

function cloneCameraAdjust(adjust: ShotUniverseCameraAdjust): ShotUniverseCameraAdjust {
  return {
    position: [...adjust.position] as [number, number, number],
    target: [...adjust.target] as [number, number, number],
    fov: adjust.fov,
    roll: Number(adjust.roll) || 0,
    offset: Array.isArray(adjust.offset)
      ? ([Number(adjust.offset[0]) || 0, Number(adjust.offset[1]) || 0] as [number, number])
      : [0, 0],
  };
}

function cameraAdjustStorageKey(packageId: string): string {
  return `shotUniverse.cameraAdjust.${packageId}`;
}

function loadCameraAdjust(packageId: string): ShotUniverseCameraAdjust {
  try {
    const raw = localStorage.getItem(cameraAdjustStorageKey(packageId));
    if (!raw) return cloneCameraAdjust(DEFAULT_CAMERA_ADJUST);
    const parsed = JSON.parse(raw);
    const position = Array.isArray(parsed?.position) ? parsed.position : [];
    const target = Array.isArray(parsed?.target) ? parsed.target : [];
    const offset = Array.isArray(parsed?.offset) ? parsed.offset : [];
    return {
      position: [Number(position[0]) || 0, Number(position[1]) || 0, Number(position[2]) || 0],
      target: [Number(target[0]) || 0, Number(target[1]) || 0, Number(target[2]) || 0],
      fov: Number(parsed?.fov) || 0,
      roll: Number(parsed?.roll) || 0,
      offset: [Number(offset[0]) || 0, Number(offset[1]) || 0],
    };
  } catch {
    return cloneCameraAdjust(DEFAULT_CAMERA_ADJUST);
  }
}

function saveCameraAdjust(packageId: string, adjust: ShotUniverseCameraAdjust) {
  try {
    localStorage.setItem(cameraAdjustStorageKey(packageId), JSON.stringify(adjust));
  } catch {}
}

function clearCameraAdjust(packageId: string) {
  try {
    localStorage.removeItem(cameraAdjustStorageKey(packageId));
  } catch {}
}

function fovToVerticalDegrees(fov: unknown): number {
  const n = Number(fov);
  if (!Number.isFinite(n) || n <= 0) return 68;
  const horizontalDegrees = n <= Math.PI * 2 ? (n * 180) / Math.PI : n;
  const assumedSourceAspect = 4 / 3;
  const horizontalRadians = (horizontalDegrees * Math.PI) / 180;
  return (
    (2 * Math.atan(Math.tan(horizontalRadians / 2) / assumedSourceAspect) * 180) /
    Math.PI
  );
}

function findCvCameraSegment(cvJson: CvJson | null | undefined, shotAbsMs: number | null) {
  const segments = (cvJson as any)?.camera?.cameraSegments;
  if (!Array.isArray(segments) || !segments.length) return null;
  if (typeof shotAbsMs === "number" && Number.isFinite(shotAbsMs)) {
    const match = segments.find((segment: any) => {
      const s = Number(segment?.s);
      const e = Number(segment?.e);
      return Number.isFinite(s) && Number.isFinite(e) && shotAbsMs >= s && shotAbsMs <= e;
    });
    if (match) return match;
  }
  return segments[0];
}

function buildCourtFitClicksFromCv(args: {
  cvJson: CvJson | null | undefined;
  shotAbsMs: number | null;
  videoWidth: number;
  videoHeight: number;
}): CourtFitClick[] {
  const segment = findCvCameraSegment(args.cvJson, args.shotAbsMs);
  const courtPoints = segment?.court_points ?? (args.cvJson as any)?.camera?.court_points;
  if (!Array.isArray(courtPoints) || args.videoWidth <= 0 || args.videoHeight <= 0) return [];
  return normalizeCourtFitClicks(
    courtPoints
      .slice(0, PBV_CV_COURT_POINT_IDS.length)
      .map((point: any, index: number) => {
        const u = Number(point?.u);
        const v = Number(point?.v);
        const confidence = Number(point?.confidence);
        if (!Number.isFinite(u) || !Number.isFinite(v)) return null;
        if (Number.isFinite(confidence) && confidence <= 0) return null;
        return {
          pointId: PBV_CV_COURT_POINT_IDS[index],
          x: u * args.videoWidth,
          y: v * args.videoHeight,
        };
      })
      .filter(Boolean),
  );
}

function getRawVideoCameraParts(cvJson: CvJson | null | undefined, shotAbsMs: number | null) {
  const segment = findCvCameraSegment(cvJson, shotAbsMs);
  const cameraSource = segment ?? (cvJson as any)?.camera ?? null;
  const rawPosition = cameraSource?.position;
  const position =
    rawPosition &&
    typeof rawPosition.x === "number" &&
    typeof rawPosition.y === "number"
      ? pointToThree({
          x: rawPosition.x,
          y: rawPosition.y,
          z: typeof rawPosition.z === "number" ? rawPosition.z : 6,
        })
      : new Vector3(0, 7, 48);
  const orientation = cameraSource?.orientation;
  const yaw = Number(orientation?.yaw);
  const pitch = Number(orientation?.pitch);
  const target =
    Number.isFinite(yaw) && Number.isFinite(pitch)
      ? (() => {
          const forwardPb = {
            x: Math.cos(pitch) * Math.cos(yaw),
            y: Math.cos(pitch) * Math.sin(yaw),
            z: Math.sin(pitch),
          };
          const distance = 60;
          return new Vector3(
            position.x + forwardPb.x * distance,
            position.y + forwardPb.z * distance,
            position.z + forwardPb.y * distance,
          );
        })()
      : new Vector3(0, 0, 0);

  return {
    position,
    target,
    fov: fovToVerticalDegrees(cameraSource?.fov),
  };
}

function buildCameraForCourtFit(args: {
  cvJson: CvJson | null | undefined;
  shotAbsMs: number | null;
  adjust: ShotUniverseCameraAdjust;
  videoWidth: number;
  videoHeight: number;
  bleedPx: number;
}) {
  const raw = getRawVideoCameraParts(args.cvJson, args.shotAbsMs);
  const renderWidth = args.videoWidth + args.bleedPx * 2;
  const renderHeight = args.videoHeight + args.bleedPx * 2;
  const camera = new PerspectiveCamera(
    clampCalibrationValue(raw.fov + args.adjust.fov, 20, 120),
    renderWidth / renderHeight,
    0.1,
    200,
  );
  camera.position.set(
    raw.position.x + args.adjust.position[0],
    raw.position.y + args.adjust.position[1],
    raw.position.z + args.adjust.position[2],
  );
  camera.lookAt(
    raw.target.x + args.adjust.target[0],
    raw.target.y + args.adjust.target[1],
    raw.target.z + args.adjust.target[2],
  );
  if (args.adjust.roll) {
    camera.rotateZ((args.adjust.roll * Math.PI) / 180);
  }
  if (args.bleedPx > 0) {
    const offset = Array.isArray(args.adjust.offset) ? args.adjust.offset : [0, 0];
    camera.setViewOffset(
      Math.max(1, args.videoWidth),
      Math.max(1, args.videoHeight),
      -args.bleedPx + (Number(offset[0]) || 0),
      -args.bleedPx + (Number(offset[1]) || 0),
      Math.max(1, renderWidth),
      Math.max(1, renderHeight),
    );
  } else {
    camera.clearViewOffset();
  }
  camera.updateMatrixWorld();
  camera.updateProjectionMatrix();
  return camera;
}

function projectCourtFitPoint(args: {
  world: Vector3;
  camera: PerspectiveCamera;
  width: number;
  height: number;
}) {
  const projected = args.world.clone().project(args.camera);
  return {
    x: ((projected.x + 1) / 2) * args.width,
    y: ((1 - projected.y) / 2) * args.height,
  };
}

function closestPointOnLinePx(point: { x: number; y: number }, a: { x: number; y: number }, b: { x: number; y: number }) {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const lenSq = abx * abx + aby * aby;
  if (lenSq <= 0.0001) {
    return { x: a.x, y: a.y, distance: Math.hypot(point.x - a.x, point.y - a.y) };
  }
  const t = ((point.x - a.x) * abx + (point.y - a.y) * aby) / lenSq;
  const x = a.x + abx * t;
  const y = a.y + aby * t;
  return { x, y, distance: Math.hypot(point.x - x, point.y - y) };
}

function isNearBaselineLineClick(pointId: CourtFitPointId) {
  return pointId === "nearBaselineAnyLeft" || pointId === "nearBaselineAnyRight";
}

function isNearLeftSidelineClick(pointId: CourtFitPointId) {
  return pointId === "nearLeftSidelineAnyNear" || pointId === "nearLeftSidelineAnyFar";
}

function isNearRightSidelineClick(pointId: CourtFitPointId) {
  return pointId === "nearRightSidelineAnyNear" || pointId === "nearRightSidelineAnyFar";
}

function isCourtLineOnlyClick(pointId: CourtFitPointId) {
  return (
    isNearBaselineLineClick(pointId) ||
    isNearLeftSidelineClick(pointId) ||
    isNearRightSidelineClick(pointId)
  );
}

function cameraAdjustDeltaMagnitude(a: ShotUniverseCameraAdjust, b: ShotUniverseCameraAdjust): number {
  const offsetA = a.offset ?? [0, 0];
  const offsetB = b.offset ?? [0, 0];
  return (
    a.position.reduce((sum, value, index) => sum + Math.abs(value - b.position[index]), 0) +
    a.target.reduce((sum, value, index) => sum + Math.abs(value - b.target[index]), 0) +
    Math.abs((a.fov ?? 0) - (b.fov ?? 0)) +
    Math.abs((a.roll ?? 0) - (b.roll ?? 0)) +
    Math.abs((offsetA[0] ?? 0) - (offsetB[0] ?? 0)) / 24 +
    Math.abs((offsetA[1] ?? 0) - (offsetB[1] ?? 0)) / 24
  );
}

function solveLinearSystem(matrix: number[][], rhs: number[]): number[] | null {
  const n = rhs.length;
  const a = matrix.map((row, i) => [...row, rhs[i]]);
  for (let col = 0; col < n; col += 1) {
    let pivot = col;
    for (let row = col + 1; row < n; row += 1) {
      if (Math.abs(a[row][col]) > Math.abs(a[pivot][col])) pivot = row;
    }
    if (Math.abs(a[pivot][col]) < 1e-8) return null;
    if (pivot !== col) {
      const tmp = a[col];
      a[col] = a[pivot];
      a[pivot] = tmp;
    }
    const divisor = a[col][col];
    for (let j = col; j <= n; j += 1) a[col][j] /= divisor;
    for (let row = 0; row < n; row += 1) {
      if (row === col) continue;
      const factor = a[row][col];
      for (let j = col; j <= n; j += 1) a[row][j] -= factor * a[col][j];
    }
  }
  return a.map((row) => row[n]);
}

function invertHomography(h: CourtHomography): CourtHomography | null {
  const a = h[0], b = h[1], c = h[2];
  const d = h[3], e = h[4], f = h[5];
  const g = h[6], i = h[7], j = h[8];
  const det =
    a * (e * j - f * i) -
    b * (d * j - f * g) +
    c * (d * i - e * g);
  if (Math.abs(det) < 1e-8) return null;
  return [
    (e * j - f * i) / det,
    (c * i - b * j) / det,
    (b * f - c * e) / det,
    (f * g - d * j) / det,
    (a * j - c * g) / det,
    (c * d - a * f) / det,
    (d * i - e * g) / det,
    (b * g - a * i) / det,
    (a * e - b * d) / det,
  ];
}

function planarEquationCount(clicks: CourtFitClick[]): number {
  return clicks.reduce((count, click) => count + (isCourtLineOnlyClick(click.pointId) ? 1 : 2), 0);
}

function fitCourtHomography(clicks: CourtFitClick[]): { homography: CourtHomography; errorPx: number; used: number; equations: number } | null {
  const constraints = clicks
    .map((click) => ({
      click,
      landmark: COURT_FIT_LANDMARKS.find((landmark) => landmark.id === click.pointId) ?? null,
    }))
    .filter(
      (entry): entry is { click: CourtFitClick; landmark: CourtFitLandmark } =>
        !!entry.landmark,
    );
  const equationCount = planarEquationCount(clicks);
  if (equationCount < 8) return null;

  const normal = Array.from({ length: 8 }, () => Array.from({ length: 8 }, () => 0));
  const rhs = Array.from({ length: 8 }, () => 0);
  const addEquation = (row: number[], value: number) => {
    for (let i = 0; i < 8; i += 1) {
      rhs[i] += row[i] * value;
      for (let j = 0; j < 8; j += 1) {
        normal[i][j] += row[i] * row[j];
      }
    }
  };

  for (const { click, landmark } of constraints) {
    const u = click.x;
    const v = click.y;
    const x = landmark.world.x;
    const z = landmark.world.z;
    if (isNearBaselineLineClick(click.pointId)) {
      addEquation([0, 0, 0, u, v, 1, -22 * u, -22 * v], 22);
    } else if (isNearLeftSidelineClick(click.pointId)) {
      addEquation([u, v, 1, 0, 0, 0, 10 * u, 10 * v], -10);
    } else if (isNearRightSidelineClick(click.pointId)) {
      addEquation([u, v, 1, 0, 0, 0, -10 * u, -10 * v], 10);
    } else {
      addEquation([u, v, 1, 0, 0, 0, -x * u, -x * v], x);
      addEquation([0, 0, 0, u, v, 1, -z * u, -z * v], z);
    }
  }

  const solved = solveLinearSystem(normal, rhs);
  if (!solved) return null;
  const imageToCourt: CourtHomography = [
    solved[0],
    solved[1],
    solved[2],
    solved[3],
    solved[4],
    solved[5],
    solved[6],
    solved[7],
    1,
  ];
  const homography = invertHomography(imageToCourt);
  if (!homography) return null;
  let total = 0;
  let usedForError = 0;
  for (const { click, landmark } of constraints) {
    if (isCourtLineOnlyClick(click.pointId)) continue;
    const projected = projectCourtHomography(homography, landmark.world.x, landmark.world.z);
    total += Math.hypot(projected.x - click.x, projected.y - click.y);
    usedForError += 1;
  }
  return {
    homography,
    errorPx: usedForError ? total / usedForError : 0,
    used: constraints.length,
    equations: equationCount,
  };
}

function projectCourtHomography(homography: CourtHomography, x: number, z: number) {
  const d = homography[6] * x + homography[7] * z + homography[8];
  if (Math.abs(d) < 1e-8) return { x: 0, y: 0 };
  return {
    x: (homography[0] * x + homography[1] * z + homography[2]) / d,
    y: (homography[3] * x + homography[4] * z + homography[5]) / d,
  };
}

function courtHomographyPolyline(homography: CourtHomography, points: Array<[number, number]>) {
  return points
    .map(([x, z]) => {
      const p = projectCourtHomography(homography, x, z);
      return `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
    })
    .join(" ");
}

function parsePolylinePoints(points: string): Array<{ x: number; y: number }> {
  return points
    .trim()
    .split(/\s+/)
    .map((pair) => {
      const [x, y] = pair.split(",").map(Number);
      return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
    })
    .filter((point): point is { x: number; y: number } => !!point);
}

function liveProjectionErrorStats(
  homography: CourtHomography | null,
  projection: ShotUniverseVideoCourtProjection | null,
): { averagePx: number; p90Px: number; maxPx: number; worstLine: string } | null {
  if (!homography || !projection) return null;
  const projectedLines = [projection.outer, ...projection.lines];
  const errors: number[] = [];
  let total = 0;
  let count = 0;
  let maxPx = 0;
  let worstLine = "";
  COURT_DEBUG_POLYLINES.forEach((courtLine, index) => {
    const livePoints = parsePolylinePoints(projectedLines[index] ?? "");
    const fitPoints = parsePolylinePoints(courtHomographyPolyline(homography, courtLine));
    const n = Math.min(livePoints.length, fitPoints.length);
    for (let i = 0; i < n; i += 1) {
      const errorPx = Math.hypot(livePoints[i].x - fitPoints[i].x, livePoints[i].y - fitPoints[i].y);
      total += errorPx;
      errors.push(errorPx);
      if (errorPx > maxPx) {
        maxPx = errorPx;
        worstLine = COURT_DEBUG_POLYLINE_LABELS[index] ?? `line ${index + 1}`;
      }
      count += 1;
    }
  });
  if (!count) return null;
  errors.sort((a, b) => a - b);
  const p90Index = Math.min(errors.length - 1, Math.ceil(errors.length * 0.9) - 1);
  return { averagePx: total / count, p90Px: errors[p90Index] ?? maxPx, maxPx, worstLine };
}

function vectorLength(v: [number, number, number]) {
  return Math.hypot(v[0], v[1], v[2]);
}

function normalizeVector(v: [number, number, number]): [number, number, number] {
  const len = vectorLength(v);
  if (len < 1e-8) return [0, 0, 0];
  return [v[0] / len, v[1] / len, v[2] / len];
}

function crossVector(a: [number, number, number], b: [number, number, number]): [number, number, number] {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function dotVector(a: [number, number, number], b: [number, number, number]) {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function deriveCameraAdjustFromHomography(args: {
  homography: CourtHomography;
  cvJson: CvJson | null | undefined;
  shotAbsMs: number | null;
  currentAdjust: ShotUniverseCameraAdjust;
  videoWidth: number;
  videoHeight: number;
}): ShotUniverseCameraAdjust | null {
  const raw = getRawVideoCameraParts(args.cvJson, args.shotAbsMs);
  const fov = clampCalibrationValue(raw.fov + (args.currentAdjust.fov ?? 0), 20, 120);
  const fy = args.videoHeight / (2 * Math.tan((fov * Math.PI) / 360));
  const fx = fy;
  const cx = args.videoWidth / 2;
  const cy = args.videoHeight / 2;
  const h = args.homography;
  const colX: [number, number, number] = [
    (h[0] - cx * h[6]) / fx,
    (h[3] - cy * h[6]) / fy,
    h[6],
  ];
  const colZ: [number, number, number] = [
    (h[1] - cx * h[7]) / fx,
    (h[4] - cy * h[7]) / fy,
    h[7],
  ];
  const colT: [number, number, number] = [
    (h[2] - cx * h[8]) / fx,
    (h[5] - cy * h[8]) / fy,
    h[8],
  ];
  const baseScale = 2 / Math.max(1e-8, vectorLength(colX) + vectorLength(colZ));

  const candidates = [baseScale, -baseScale]
    .map((scale) => {
      const rX0 = normalizeVector([colX[0] * scale, colX[1] * scale, colX[2] * scale]);
      const zScaled: [number, number, number] = [
        colZ[0] * scale,
        colZ[1] * scale,
        colZ[2] * scale,
      ];
      const zMinusX: [number, number, number] = [
        zScaled[0] - rX0[0] * dotVector(zScaled, rX0),
        zScaled[1] - rX0[1] * dotVector(zScaled, rX0),
        zScaled[2] - rX0[2] * dotVector(zScaled, rX0),
      ];
      const rZ0 = normalizeVector(zMinusX);
      const rY0 = normalizeVector(crossVector(rZ0, rX0));
      const t: [number, number, number] = [
        colT[0] * scale,
        colT[1] * scale,
        colT[2] * scale,
      ];
      const position = new Vector3(
        -(rX0[0] * t[0] + rX0[1] * t[1] + rX0[2] * t[2]),
        -(rY0[0] * t[0] + rY0[1] * t[1] + rY0[2] * t[2]),
        -(rZ0[0] * t[0] + rZ0[1] * t[1] + rZ0[2] * t[2]),
      );
      const forward = new Vector3(rX0[2], rY0[2], rZ0[2]).normalize();
      const target = position.clone().addScaledVector(forward, 60);
      return { position, target };
    })
    .filter((candidate) => Number.isFinite(candidate.position.x));

  const chosen =
    candidates.find((candidate) => candidate.position.y > 1) ??
    candidates.sort((a, b) => b.position.y - a.position.y)[0];
  if (!chosen) return null;

  return {
    position: [
      clampCalibrationValue(chosen.position.x - raw.position.x, -35, 35),
      clampCalibrationValue(chosen.position.y - raw.position.y, -35, 35),
      clampCalibrationValue(chosen.position.z - raw.position.z, -35, 35),
    ] as [number, number, number],
    target: [
      clampCalibrationValue(chosen.target.x - raw.target.x, -35, 35),
      clampCalibrationValue(chosen.target.y - raw.target.y, -35, 35),
      clampCalibrationValue(chosen.target.z - raw.target.z, -35, 35),
    ] as [number, number, number],
    fov: clampCalibrationValue(fov - raw.fov, -35, 35),
    roll: 0,
    offset: [0, 0],
  };
}

function buildCourtLineSamples(): Array<{ world: Vector3; weight: number }> {
  const samples: Array<{ world: Vector3; weight: number }> = [];
  const add = (x: number, z: number, weight = 1) => {
    samples.push({ world: new Vector3(x, 0, z), weight });
  };
  const addLine = (
    from: [number, number],
    to: [number, number],
    count: number,
    weight = 1,
  ) => {
    for (let i = 0; i < count; i += 1) {
      const t = count <= 1 ? 0 : i / (count - 1);
      add(from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t, weight);
    }
  };

  addLine([-10, 22], [10, 22], 9, 1.5);
  addLine([-10, -22], [10, -22], 9, 0.7);
  addLine([-10, -22], [-10, 22], 13, 1.2);
  addLine([10, -22], [10, 22], 13, 1.2);
  addLine([-10, 0], [10, 0], 9, 1.45);
  addLine([-10, 7], [10, 7], 9, 1.35);
  addLine([-10, -7], [10, -7], 9, 0.8);
  addLine([0, 7], [0, 22], 7, 1.1);
  addLine([0, -22], [0, -7], 7, 0.7);
  return samples;
}

function solveCameraToHomography(args: {
  homography: CourtHomography;
  cvJson: CvJson | null | undefined;
  shotAbsMs: number | null;
  currentAdjust: ShotUniverseCameraAdjust;
  videoWidth: number;
  videoHeight: number;
  bleedPx: number;
}): { adjust: ShotUniverseCameraAdjust; errorPx: number; initialErrorPx: number } | null {
  if (args.videoWidth <= 0 || args.videoHeight <= 0) return null;
  const renderWidth = args.videoWidth + args.bleedPx * 2;
  const renderHeight = args.videoHeight + args.bleedPx * 2;
  const samples = buildCourtLineSamples();
  if (!samples.length) return null;

  const evaluate = (adjust: ShotUniverseCameraAdjust) => {
    const camera = buildCameraForCourtFit({
      cvJson: args.cvJson,
      shotAbsMs: args.shotAbsMs,
      adjust,
      videoWidth: args.videoWidth,
      videoHeight: args.videoHeight,
      bleedPx: args.bleedPx,
    });
    let weightedTotal = 0;
    let weightTotal = 0;
    let maxError = 0;
    for (const sample of samples) {
      const target = projectCourtHomography(
        args.homography,
        sample.world.x,
        sample.world.z,
      );
      const projected = projectCourtFitPoint({
        world: sample.world,
        camera,
        width: renderWidth,
        height: renderHeight,
      });
      const dx = projected.x - args.bleedPx - target.x;
      const dy = projected.y - args.bleedPx - target.y;
      const distance = Math.min(10000, Math.hypot(dx, dy));
      weightedTotal += distance * sample.weight;
      weightTotal += sample.weight;
      maxError = Math.max(maxError, distance);
    }
    return weightedTotal / Math.max(1, weightTotal) + maxError * 0.45;
  };

  const makeCandidate = (
    adjust: ShotUniverseCameraAdjust,
    kind: "position" | "target" | "fov" | "roll" | "offset",
    index: 0 | 1 | 2,
    delta: number,
  ) => {
    const next = cloneCameraAdjust(adjust);
    if (kind === "fov") {
      next.fov = clampCalibrationValue(next.fov + delta, -35, 35);
    } else if (kind === "roll") {
      next.roll = clampCalibrationValue((next.roll ?? 0) + delta, -20, 20);
    } else if (kind === "offset") {
      const offset = next.offset ?? [0, 0];
      const i = index === 0 ? 0 : 1;
      offset[i] = clampCalibrationValue(offset[i] + delta, -240, 240);
      next.offset = [offset[0], offset[1]];
    } else {
      next[kind][index] = clampCalibrationValue(next[kind][index] + delta, -35, 35);
    }
    return next;
  };

  const initialErrorPx = evaluate(args.currentAdjust);
  const seeds = [cloneCameraAdjust(args.currentAdjust), cloneCameraAdjust(DEFAULT_CAMERA_ADJUST)];
  const decomposed = deriveCameraAdjustFromHomography({
    homography: args.homography,
    cvJson: args.cvJson,
    shotAbsMs: args.shotAbsMs,
    currentAdjust: args.currentAdjust,
    videoWidth: args.videoWidth,
    videoHeight: args.videoHeight,
  });
  if (decomposed) seeds.push(decomposed);

  let best = seeds
    .map((adjust) => ({ adjust, score: evaluate(adjust) }))
    .sort((a, b) => a.score - b.score)[0];
  if (!best) return null;

  const steps = [16, 8, 4, 2, 1, 0.5, 0.25, 0.1];
  for (const step of steps) {
    let improved = true;
    let guard = 0;
    while (improved && guard < 120) {
      improved = false;
      guard += 1;
      const moves: Array<["position" | "target" | "fov" | "roll" | "offset", 0 | 1 | 2, number]> = [
        ["position", 0, -step],
        ["position", 0, step],
        ["position", 1, -step],
        ["position", 1, step],
        ["position", 2, -step],
        ["position", 2, step],
        ["target", 0, -step],
        ["target", 0, step],
        ["target", 1, -step],
        ["target", 1, step],
        ["target", 2, -step],
        ["target", 2, step],
        ["fov", 0, -step * 1.6],
        ["fov", 0, step * 1.6],
        ["roll", 0, -step * 0.8],
        ["roll", 0, step * 0.8],
        ["offset", 0, -step * 8],
        ["offset", 0, step * 8],
        ["offset", 1, -step * 8],
        ["offset", 1, step * 8],
      ];
      for (const [kind, index, delta] of moves) {
        const candidate = makeCandidate(best.adjust, kind, index, delta);
        const score = evaluate(candidate);
        if (score + 0.02 < best.score) {
          best = { adjust: candidate, score };
          improved = true;
        }
      }
    }
  }

  return { adjust: best.adjust, errorPx: best.score, initialErrorPx };
}

function solveCourtFit(args: {
  cvJson: CvJson | null | undefined;
  shotAbsMs: number | null;
  currentAdjust: ShotUniverseCameraAdjust;
  clicks: CourtFitClick[];
  videoWidth: number;
  videoHeight: number;
  bleedPx: number;
}): { adjust: ShotUniverseCameraAdjust; errorPx: number; residuals: CourtFitResidual[] } | null {
  const used = args.clicks
    .map((click) => ({
      click,
      landmark: COURT_FIT_LANDMARKS.find((landmark) => landmark.id === click.pointId) ?? null,
    }))
    .filter((entry): entry is { click: CourtFitClick; landmark: CourtFitLandmark } => !!entry.landmark);
  if (used.length < 4 || args.videoWidth <= 0 || args.videoHeight <= 0) return null;

  const renderWidth = args.videoWidth + args.bleedPx * 2;
  const renderHeight = args.videoHeight + args.bleedPx * 2;
  const evaluate = (adjust: ShotUniverseCameraAdjust) => {
    const camera = buildCameraForCourtFit({
      cvJson: args.cvJson,
      shotAbsMs: args.shotAbsMs,
      adjust,
      videoWidth: args.videoWidth,
      videoHeight: args.videoHeight,
      bleedPx: args.bleedPx,
    });
    const residuals: CourtFitResidual[] = [];
    let weightedTotal = 0;
    let weightTotal = 0;
    let maxError = 0;
    const baselineLeft = projectCourtFitPoint({
      world: new Vector3(-10, 0, 22),
      camera,
      width: renderWidth,
      height: renderHeight,
    });
    const baselineRight = projectCourtFitPoint({
      world: new Vector3(10, 0, 22),
      camera,
      width: renderWidth,
      height: renderHeight,
    });
    const baselineA = {
      x: baselineLeft.x - args.bleedPx,
      y: baselineLeft.y - args.bleedPx,
    };
    const baselineB = {
      x: baselineRight.x - args.bleedPx,
      y: baselineRight.y - args.bleedPx,
    };
    const leftSidelineNear = projectCourtFitPoint({
      world: new Vector3(-10, 0, 22),
      camera,
      width: renderWidth,
      height: renderHeight,
    });
    const leftSidelineFar = projectCourtFitPoint({
      world: new Vector3(-10, 0, 0),
      camera,
      width: renderWidth,
      height: renderHeight,
    });
    const rightSidelineNear = projectCourtFitPoint({
      world: new Vector3(10, 0, 22),
      camera,
      width: renderWidth,
      height: renderHeight,
    });
    const rightSidelineFar = projectCourtFitPoint({
      world: new Vector3(10, 0, 0),
      camera,
      width: renderWidth,
      height: renderHeight,
    });
    const leftSidelineA = {
      x: leftSidelineNear.x - args.bleedPx,
      y: leftSidelineNear.y - args.bleedPx,
    };
    const leftSidelineB = {
      x: leftSidelineFar.x - args.bleedPx,
      y: leftSidelineFar.y - args.bleedPx,
    };
    const rightSidelineA = {
      x: rightSidelineNear.x - args.bleedPx,
      y: rightSidelineNear.y - args.bleedPx,
    };
    const rightSidelineB = {
      x: rightSidelineFar.x - args.bleedPx,
      y: rightSidelineFar.y - args.bleedPx,
    };
    for (const { click, landmark } of used) {
      const p = projectCourtFitPoint({
        world: landmark.world,
        camera,
        width: renderWidth,
        height: renderHeight,
      });
      const clicked = { x: click.x, y: click.y };
      let fit = { x: p.x - args.bleedPx, y: p.y - args.bleedPx, distance: 0 };
      let constraint: CourtFitResidual["constraint"] = "point";
      let weight = 1.65;
      if (isNearBaselineLineClick(click.pointId)) {
        fit = closestPointOnLinePx(clicked, baselineA, baselineB);
        constraint = "line";
        weight = 0.9;
      } else if (isNearLeftSidelineClick(click.pointId)) {
        fit = closestPointOnLinePx(clicked, leftSidelineA, leftSidelineB);
        constraint = "line";
        weight = 0.9;
      } else if (isNearRightSidelineClick(click.pointId)) {
        fit = closestPointOnLinePx(clicked, rightSidelineA, rightSidelineB);
        constraint = "line";
        weight = 0.9;
      } else {
        fit.distance = Math.hypot(fit.x - click.x, fit.y - click.y);
      }
      residuals.push({
        pointId: click.pointId,
        label: landmark.label,
        clickX: click.x,
        clickY: click.y,
        fitX: fit.x,
        fitY: fit.y,
        errorPx: fit.distance,
        constraint,
      });
      weightedTotal += fit.distance * weight;
      weightTotal += weight;
      maxError = Math.max(maxError, fit.distance);
    }
    return {
      residuals,
      score: weightedTotal / Math.max(1, weightTotal) + maxError * 0.18,
      averageError: residuals.reduce((sum, residual) => sum + residual.errorPx, 0) / Math.max(1, residuals.length),
    };
  };
  const score = (adjust: ShotUniverseCameraAdjust) => evaluate(adjust).score;

  const makeCandidate = (
    adjust: ShotUniverseCameraAdjust,
    kind: "position" | "target" | "fov" | "roll" | "offset",
    index: 0 | 1 | 2,
    delta: number,
  ) => {
    const next = cloneCameraAdjust(adjust);
    if (kind === "fov") {
      next.fov = clampCalibrationValue(next.fov + delta, -35, 35);
    } else if (kind === "roll") {
      next.roll = clampCalibrationValue((next.roll ?? 0) + delta, -20, 20);
    } else if (kind === "offset") {
      const offset = next.offset ?? [0, 0];
      const i = index === 0 ? 0 : 1;
      offset[i] = clampCalibrationValue(offset[i] + delta, -240, 240);
      next.offset = [offset[0], offset[1]];
    } else {
      next[kind][index] = clampCalibrationValue(next[kind][index] + delta, -35, 35);
    }
    return next;
  };

  let best = cloneCameraAdjust(args.currentAdjust);
  let bestScore = score(best);
  const spatialSteps = [4, 2, 1, 0.5, 0.25, 0.1];
  for (const step of spatialSteps) {
    let improved = true;
    let guard = 0;
    while (improved && guard < 80) {
      improved = false;
      guard += 1;
      const moves: Array<["position" | "target" | "fov" | "roll" | "offset", 0 | 1 | 2, number]> = [
        ["position", 0, -step],
        ["position", 0, step],
        ["position", 1, -step],
        ["position", 1, step],
        ["position", 2, -step],
        ["position", 2, step],
        ["target", 0, -step],
        ["target", 0, step],
        ["target", 1, -step],
        ["target", 1, step],
        ["target", 2, -step],
        ["target", 2, step],
        ["fov", 0, -step * 1.6],
        ["fov", 0, step * 1.6],
        ["roll", 0, -step * 0.8],
        ["roll", 0, step * 0.8],
        ["offset", 0, -step * 8],
        ["offset", 0, step * 8],
        ["offset", 1, -step * 8],
        ["offset", 1, step * 8],
      ];
      for (const [kind, index, delta] of moves) {
        const candidate = makeCandidate(best, kind, index, delta);
        const candidateScore = score(candidate);
        if (candidateScore + 0.02 < bestScore) {
          best = candidate;
          bestScore = candidateScore;
          improved = true;
        }
      }
    }
  }

  const finalEval = evaluate(best);
  return { adjust: best, errorPx: finalEval.averageError, residuals: finalEval.residuals };
}

function formatNumber(n: number): string {
  return Math.abs(n) >= 10 ? n.toFixed(0) : n.toFixed(1);
}

function formatDebugMs(ms: number | null): string {
  if (ms == null || !Number.isFinite(ms)) return "n/a";
  return `${Math.round(ms)}ms (${(ms / 1000).toFixed(2)}s)`;
}

function formatDebugSec(sec: number | null): string {
  if (sec == null || !Number.isFinite(sec)) return "n/a";
  return `${sec.toFixed(2)}s`;
}

function cloneShotSwitchTiming(
  timing: ShotSwitchTimingTuning,
): ShotSwitchTimingTuning {
  return { ...timing };
}

function normalizeShotSwitchTiming(
  timing: Partial<ShotSwitchTimingTuning> | null | undefined,
): ShotSwitchTimingTuning {
  return {
    fraction: Number.isFinite(timing?.fraction)
      ? Number(timing?.fraction)
      : DEFAULT_SHOT_SWITCH_TIMING.fraction,
    holdCurrentFraction: Number.isFinite(timing?.holdCurrentFraction)
      ? Number(timing?.holdCurrentFraction)
      : DEFAULT_SHOT_SWITCH_TIMING.holdCurrentFraction,
    previewNextFraction: Number.isFinite(timing?.previewNextFraction)
      ? Number(timing?.previewNextFraction)
      : DEFAULT_SHOT_SWITCH_TIMING.previewNextFraction,
  };
}

function clampCalibrationValue(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function clampSwitchTimingValue(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
}

function getShotSwitchMs(
  previousContactMs: number,
  nextContactMs: number,
  timing: ShotSwitchTimingTuning,
): number {
  if (!Number.isFinite(previousContactMs) || !Number.isFinite(nextContactMs)) {
    return NaN;
  }
  const normalizedTiming = normalizeShotSwitchTiming(timing);
  const intervalMs = Math.max(0, nextContactMs - previousContactMs);
  const rawSwitchMs =
    previousContactMs +
    intervalMs * clampSwitchTimingValue(normalizedTiming.fraction, 0, 1);
  const minSwitchMs =
    previousContactMs +
    intervalMs * clampSwitchTimingValue(normalizedTiming.holdCurrentFraction, 0, 0.5);
  const maxSwitchMs =
    nextContactMs -
    intervalMs * clampSwitchTimingValue(normalizedTiming.previewNextFraction, 0, 0.5);
  if (maxSwitchMs < minSwitchMs) {
    return Math.max(previousContactMs, Math.min(nextContactMs, rawSwitchMs));
  }
  return Math.max(minSwitchMs, Math.min(maxSwitchMs, rawSwitchMs));
}

function getContainedRect(args: {
  containerWidth: number;
  containerHeight: number;
  contentWidth: number;
  contentHeight: number;
}) {
  const { containerWidth, containerHeight, contentWidth, contentHeight } = args;
  if (
    containerWidth <= 0 ||
    containerHeight <= 0 ||
    contentWidth <= 0 ||
    contentHeight <= 0
  ) {
    return null;
  }

  const containerAspect = containerWidth / containerHeight;
  const contentAspect = contentWidth / contentHeight;
  if (!Number.isFinite(containerAspect) || !Number.isFinite(contentAspect)) {
    return null;
  }

  if (containerAspect > contentAspect) {
    const height = containerHeight;
    const width = height * contentAspect;
    return {
      left: (containerWidth - width) / 2,
      top: 0,
      width,
      height,
    };
  }

  const width = containerWidth;
  const height = width / contentAspect;
  return {
    left: 0,
    top: (containerHeight - height) / 2,
    width,
    height,
  };
}

function nearestShotOverlayForTime(
  row: KitchenArrivalRow | null | undefined,
  timeSec: number,
) {
  const overlays = row?.shotNumberOverlays ?? [];
  const currentMsRel = timeSec * 1000;
  let best: ShotNumberOverlay | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const overlay of overlays) {
    const atMsRel = getShotContactMsRel(row, overlay);
    if (!Number.isFinite(atMsRel)) continue;
    const distance = Math.abs(atMsRel - currentMsRel);
    if (distance < bestDistance) {
      best = overlay;
      bestDistance = distance;
    }
  }
  return best;
}

function midpointShotOverlayForTime(
  row: KitchenArrivalRow | null | undefined,
  timeSec: number,
  timing: ShotSwitchTimingTuning = LEGACY_SHOT_SWITCH_TIMING,
) {
  const overlays = [...(row?.shotNumberOverlays ?? [])].sort(
    (a, b) => getShotContactMsRel(row, a) - getShotContactMsRel(row, b),
  );
  const currentMs = timeSec * 1000;
  let active: ShotNumberOverlay | null = null;
  for (let i = 0; i < overlays.length; i += 1) {
    const overlay = overlays[i];
    const contactMs = getShotContactMsRel(row, overlay);
    if (!Number.isFinite(contactMs)) continue;

    const previousContactMs =
      i > 0 ? getShotContactMsRel(row, overlays[i - 1]) : NaN;
    const switchMs = Number.isFinite(previousContactMs)
      ? getShotSwitchMs(previousContactMs, contactMs, timing)
      : 0;

    if (switchMs <= currentMs) {
      active = overlay;
    } else {
      break;
    }
  }
  return active ?? overlays[0] ?? null;
}

function contactedShotOverlayForTime(
  row: KitchenArrivalRow | null | undefined,
  timeSec: number,
) {
  const overlays = [...(row?.shotNumberOverlays ?? [])].sort(
    (a, b) => getShotContactMsRel(row, a) - getShotContactMsRel(row, b),
  );
  const currentMs = timeSec * 1000;
  let active: ShotNumberOverlay | null = null;
  for (const overlay of overlays) {
    const contactMs = getShotContactMsRel(row, overlay);
    if (!Number.isFinite(contactMs)) continue;
    if (contactMs <= currentMs + 20) {
      active = overlay;
    } else {
      break;
    }
  }
  return active ?? overlays[0] ?? null;
}

function getSortedShotOverlays(row: KitchenArrivalRow | null | undefined) {
  return [...(row?.shotNumberOverlays ?? [])].sort(
    (a, b) => getShotContactMsRel(row, a) - getShotContactMsRel(row, b),
  );
}

function getSwitchMsForOverlayIndex(
  row: KitchenArrivalRow | null | undefined,
  overlays: ShotNumberOverlay[],
  index: number,
  timing: ShotSwitchTimingTuning = LEGACY_SHOT_SWITCH_TIMING,
): number {
  if (index <= 0) return 0;
  const previousContactMs = getShotContactMsRel(row, overlays[index - 1]);
  const contactMs = getShotContactMsRel(row, overlays[index]);
  if (!Number.isFinite(previousContactMs) || !Number.isFinite(contactMs)) {
    return NaN;
  }
  return getShotSwitchMs(previousContactMs, contactMs, timing);
}

function getRowOverlayBaseMs(row: KitchenArrivalRow | null | undefined): number {
  const firstAtMsRel = Number(row?.shotNumberOverlays?.[0]?.atMsRel);
  return Number.isFinite(firstAtMsRel) ? firstAtMsRel : 0;
}

function normalizeOverlayMsForVideo(
  row: KitchenArrivalRow | null | undefined,
  rawMs: number,
): number {
  if (!Number.isFinite(rawMs)) return NaN;

  const segmentStartMs = Number(row?.segmentStartMs ?? row?.startMs ?? NaN);
  const segmentEndMs = Number(row?.segmentEndMs ?? row?.endMs ?? NaN);
  const segmentDurationMs =
    Number.isFinite(segmentStartMs) &&
    Number.isFinite(segmentEndMs) &&
    segmentEndMs > segmentStartMs
      ? segmentEndMs - segmentStartMs
      : null;
  const baseMs = getRowOverlayBaseMs(row);

  if (Number.isFinite(segmentStartMs)) {
    const fromSegmentStart = rawMs - segmentStartMs;
    if (
      fromSegmentStart >= -500 &&
      (segmentDurationMs == null || fromSegmentStart <= segmentDurationMs + 3000)
    ) {
      return Math.max(0, fromSegmentStart);
    }
  }

  if (Number.isFinite(baseMs) && baseMs > 0) {
    const fromOverlayBase = rawMs - baseMs;
    if (
      fromOverlayBase >= -500 &&
      (segmentDurationMs == null || fromOverlayBase <= segmentDurationMs + 3000)
    ) {
      return Math.max(0, fromOverlayBase);
    }
  }

  return rawMs;
}

function getShotContactMsRel(
  row: KitchenArrivalRow | null | undefined,
  overlay: ShotNumberOverlay | null | undefined,
): number {
  const atMsRel = Number(overlay?.atMsRel);
  if (Number.isFinite(atMsRel)) return normalizeOverlayMsForVideo(row, atMsRel);

  const rawContactCandidates = [
    overlay?.trajectoryStart?.ms,
    overlay?.rawShot?.resulting_ball_movement?.trajectory?.start?.ms,
    overlay?.rawShot?.start_ms,
  ];

  for (const candidate of rawContactCandidates) {
    const rawContactMs = Number(candidate);
    if (!Number.isFinite(rawContactMs) || rawContactMs <= 0) continue;
    return normalizeOverlayMsForVideo(row, rawContactMs);
  }

  return NaN;
}

function pickShotNumberForRally(row: KitchenArrivalRow): number {
  const overlays = row.shotNumberOverlays ?? [];
  return (
    overlays.find((overlay) => overlay.shotNumber === 1)?.shotNumber ??
    overlays[0]?.shotNumber ??
    row.breakShotNumber ??
    1
  );
}

export function GameStoryShotUniversePopup(props: {
  open: boolean;
  onClose: () => void;
  packageId: string;
  row: KitchenArrivalRow | null;
  rows: KitchenArrivalRow[];
  shotNumber: number | null;
  insightsJson: InsightsJson | null | undefined;
  cvJson?: CvJson | null;
  renderPlayerIdentity?: RenderPlayerIdentity;
  insightsToCvPlayerIndex: PlayerIndexMap;
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
  opportunityContext?: ShotOpportunityLabContext | null;
  restoreBookmark?: { bookmark: ShotBookmark; nonce: number } | null;
  onWatchBookmark?: (bookmark: ShotBookmark) => void;
  onBackToOpportunities?: () => void;
}) {
  const {
    open,
    onClose,
    packageId,
    row,
    rows,
    shotNumber,
    insightsJson,
    cvJson,
    renderPlayerIdentity,
    insightsToCvPlayerIndex,
    firefightsClips,
    firefightRallySet,
    opportunityContext,
    restoreBookmark,
    onWatchBookmark,
    onBackToOpportunities,
  } = props;

  const [viewMode, setViewMode] = React.useState<ViewMode>("court3d");
  const [family] = React.useState<ShotUniverseShotFamily>("all");
  const [showActual] = React.useState(true);
  const [showGhosts] = React.useState(true);
  const [showTargets] = React.useState(true);
  const [showVideoReference, setShowVideoReference] = React.useState(true);
  // Kept wired for later, but hidden for now: the compact trajectory-shape panel
  // was competing with the clearer Shot Target Lab target/path workflow.
  const [showMiniPlot] = React.useState(false);
  const [showCalibration, setShowCalibration] = React.useState(false);
  const [showTimingTuner, setShowTimingTuner] = React.useState(true);
  const [shotSwitchTiming, setShotSwitchTiming] =
    React.useState<ShotSwitchTimingTuning>(() =>
      cloneShotSwitchTiming(DEFAULT_SHOT_SWITCH_TIMING),
    );
  const normalizedShotSwitchTiming = React.useMemo(
    () => normalizeShotSwitchTiming(shotSwitchTiming),
    [shotSwitchTiming],
  );
  const [courtRotationSteps, setCourtRotationSteps] = React.useState(0);
  const [videoPauseSnapArmed, setVideoPauseSnapArmed] = React.useState(false);
  const [pauseAtShotContacts, setPauseAtShotContacts] = React.useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = React.useState(false);
  const [playbackRate] = React.useState<0.25 | 0.5 | 1>(1);
  const [contactPausedShotNumber, setContactPausedShotNumber] =
    React.useState<number | null>(null);
  const [cameraAdjust, setCameraAdjust] = React.useState<ShotUniverseCameraAdjust>(() =>
    loadCameraAdjust(packageId),
  );
  const [videoViewResetNonce, setVideoViewResetNonce] = React.useState(0);
  const [videoRefNonce, setVideoRefNonce] = React.useState(0);
  const ignoreCameraInteractionUntilRef = React.useRef(0);
  const [courtFitActive, setCourtFitActive] = React.useState(false);
  const [courtFitClicks, setCourtFitClicks] = React.useState<CourtFitClick[]>(() =>
    loadCourtFitClicks(packageId),
  );
  const [courtFitErrorPx, setCourtFitErrorPx] = React.useState<number | null>(null);
  const [courtFitResiduals, setCourtFitResiduals] = React.useState<CourtFitResidual[]>([]);
  const [courtFitSolveStatus, setCourtFitSolveStatus] = React.useState<string | null>(null);
  const [courtHomography, setCourtHomography] = React.useState<CourtHomography | null>(null);
  const [courtHomographyStatus, setCourtHomographyStatus] = React.useState<string | null>(null);
  const [hideVideoAlignedCourtLines, setHideVideoAlignedCourtLines] = React.useState(false);
  const [videoCourtProjection, setVideoCourtProjection] =
    React.useState<ShotUniverseVideoCourtProjection | null>(null);
  const [selectedRallyIndex, setSelectedRallyIndex] = React.useState<number | null>(
    row?.rallyIndex ?? null,
  );
  const [manualShotNumber, setManualShotNumber] = React.useState<number | null>(null);
  const [liveContextCleared, setLiveContextCleared] = React.useState(false);
  const [rallyReview, setRallyReview] =
    React.useState<RallyReviewWithPipeline | null>(null);
  const [rallyReviewLoading, setRallyReviewLoading] = React.useState(false);
  const [rallyReviewError, setRallyReviewError] = React.useState<string | null>(null);
  const [rallyReviewLoopSeed, setRallyReviewLoopSeed] = React.useState<{
    enabled: boolean;
    aSec: number;
    bSec: number;
    nonce: number;
  } | null>(null);
  const [playbackSeed, setPlaybackSeed] = React.useState<{
    timeSec: number;
    paused: boolean;
    nonce: number;
  } | null>(null);
  const { bookmarks } = useShotBookmarks(packageId);
  const [bookmarksOpen, setBookmarksOpen] = React.useState(false);
  const [bookmarkEditorOpen, setBookmarkEditorOpen] = React.useState(false);
  const [bookmarkDescriptionDraft, setBookmarkDescriptionDraft] = React.useState("");
  const [labSelection, setLabSelection] = React.useState<ShotUniverseLabSelection>({
    target: null,
    pathLabel: null,
  });
  const [restoreLabSelection, setRestoreLabSelection] =
    React.useState<ShotUniverseLabSelectionRestore | null>(null);
  const restoreCurrentLabSelection = React.useCallback(() => {
    const targets = labSelection.targets?.length
      ? labSelection.targets
      : labSelection.target
        ? [labSelection.target]
        : null;
    if (!labSelection.target && !targets?.length && !labSelection.pathLabel) return;
    setRestoreLabSelection({
      nonce: Date.now(),
      target: labSelection.target,
      targets,
      pathLabel: labSelection.pathLabel,
    });
  }, [labSelection.pathLabel, labSelection.target, labSelection.targets]);
  const [debugVideoTimeSec, setDebugVideoTimeSec] = React.useState(0);
  const [debugPlaybackShotNumber, setDebugPlaybackShotNumber] =
    React.useState<number | null>(null);
  const [debugLastMidpointUpdate, setDebugLastMidpointUpdate] = React.useState<{
    shotNumber: number | null;
    videoSec: number;
  } | null>(null);
  const [debugLastLabelUpdate, setDebugLastLabelUpdate] = React.useState<{
    shotNumber: number | null;
    videoSec: number;
  } | null>(null);
  const [lingeringActualShot, setLingeringActualShot] = React.useState<{
    context: ShotUniverseContext;
    startSec: number;
    endSec: number;
    startedAtMs: number;
  } | null>(null);
  const [frame, setFrame] = React.useState({
    left: 40,
    top: 36,
    width: 1420,
    height: 820,
  });
  const dragRef = React.useRef<null | {
    startX: number;
    startY: number;
    startLeft: number;
    startTop: number;
  }>(null);
  const resizeRef = React.useRef<null | {
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
  }>(null);
  const stageRef = React.useRef<HTMLDivElement | null>(null);
  const [shotSpaceOverlayPortalEl, setShotSpaceOverlayPortalEl] =
    React.useState<HTMLDivElement | null>(null);
  const [videoControlsPortalEl, setVideoControlsPortalEl] =
    React.useState<HTMLDivElement | null>(null);
  const [videoLoopControlsPortalEl, setVideoLoopControlsPortalEl] =
    React.useState<HTMLDivElement | null>(null);
  const [videoControlsInteractionActive, setVideoControlsInteractionActive] =
    React.useState(false);
  const videoControlsInteractionActiveRef = React.useRef(false);
  const activeVideoRef = React.useRef<React.RefObject<HTMLVideoElement | null> | null>(null);
  const [videoContentRect, setVideoContentRect] = React.useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const suppressNextPauseSnapRef = React.useRef(false);
  const suppressLiveShotUpdatesUntilRef = React.useRef(0);
  const suppressLiveShotUpdatesBelowRef = React.useRef<number | null>(null);
  const lastContactCheckMsRef = React.useRef<number | null>(null);
  const lastAutoPausedShotNumberRef = React.useRef<number | null>(null);
  const lastVideoSyncedShotNumberRef = React.useRef<number | null>(null);
  const previousShotSpaceContextRef = React.useRef<ShotUniverseContext | null>(null);

  React.useEffect(() => {
    videoControlsInteractionActiveRef.current = videoControlsInteractionActive;
  }, [videoControlsInteractionActive]);

  React.useEffect(() => {
    function onMove(e: MouseEvent) {
      const drag = dragRef.current;
      if (drag) {
        const dx = e.clientX - drag.startX;
        const dy = e.clientY - drag.startY;
        setFrame((cur) => ({
          ...cur,
          left: Math.max(0, drag.startLeft + dx),
          top: Math.max(0, drag.startTop + dy),
        }));
      }
      const resize = resizeRef.current;
      if (resize) {
        const dx = e.clientX - resize.startX;
        const dy = e.clientY - resize.startY;
        setFrame((cur) => ({
          ...cur,
          width: Math.max(900, resize.startWidth + dx),
          height: Math.max(560, resize.startHeight + dy),
        }));
      }
    }
    function onUp() {
      dragRef.current = null;
      resizeRef.current = null;
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  React.useEffect(() => {
    setCameraAdjust(loadCameraAdjust(packageId));
    setCourtFitClicks(loadCourtFitClicks(packageId));
    setCourtFitErrorPx(null);
    setCourtFitResiduals([]);
    setCourtFitSolveStatus(null);
    setCourtHomography(null);
    setCourtHomographyStatus(null);
  }, [packageId]);

  React.useEffect(() => {
    setSelectedRallyIndex(row?.rallyIndex ?? null);
    setManualShotNumber(null);
    setLiveContextCleared(false);
    setVideoPauseSnapArmed(false);
    setCourtRotationSteps(0);
    setContactPausedShotNumber(null);
    setPlaybackSeed(null);
    setDebugPlaybackShotNumber(null);
    setDebugLastMidpointUpdate(null);
    setDebugLastLabelUpdate(null);
    setLingeringActualShot(null);
    setCourtFitActive(false);
    setCourtFitClicks(loadCourtFitClicks(packageId));
    setCourtFitErrorPx(null);
    setCourtFitResiduals([]);
    setCourtFitSolveStatus(null);
    setCourtHomography(null);
    setCourtHomographyStatus(null);
    setRallyReview(null);
    setRallyReviewError(null);
    setRallyReviewLoopSeed(null);
    lastContactCheckMsRef.current = null;
    lastAutoPausedShotNumberRef.current = null;
    lastVideoSyncedShotNumberRef.current = null;
    previousShotSpaceContextRef.current = null;
  }, [packageId, row?.rallyIndex, shotNumber]);

  const updateCameraAdjust = React.useCallback(
    (kind: "position" | "target", index: 0 | 1 | 2, value: number) => {
      setCameraAdjust((cur) => {
        const next = cloneCameraAdjust(cur);
        next[kind][index] = value;
        saveCameraAdjust(packageId, next);
        return next;
      });
      setVideoViewResetNonce((n) => n + 1);
    },
    [packageId],
  );

  const nudgeCameraAdjust = React.useCallback(
    (kind: "position" | "target", index: 0 | 1 | 2, delta: number, min: number, max: number) => {
      setCameraAdjust((cur) => {
        const next = cloneCameraAdjust(cur);
        next[kind][index] = clampCalibrationValue(next[kind][index] + delta, min, max);
        saveCameraAdjust(packageId, next);
        return next;
      });
      setVideoViewResetNonce((n) => n + 1);
    },
    [packageId],
  );

  const updateFovAdjust = React.useCallback(
    (value: number) => {
      setCameraAdjust((cur) => {
        const next = { ...cur, fov: clampCalibrationValue(value, -35, 35) };
        saveCameraAdjust(packageId, next);
        return next;
      });
      setVideoViewResetNonce((n) => n + 1);
    },
    [packageId],
  );

  const updateRollAdjust = React.useCallback(
    (value: number) => {
      setCameraAdjust((cur) => {
        const next = cloneCameraAdjust(cur);
        next.roll = clampCalibrationValue(value, -20, 20);
        saveCameraAdjust(packageId, next);
        return next;
      });
      setVideoViewResetNonce((n) => n + 1);
    },
    [packageId],
  );

  const nudgeOffsetAdjust = React.useCallback(
    (index: 0 | 1, delta: number) => {
      setCameraAdjust((cur) => {
        const next = cloneCameraAdjust(cur);
        const offset = next.offset ?? [0, 0];
        offset[index] = clampCalibrationValue(offset[index] + delta, -240, 240);
        next.offset = [offset[0], offset[1]];
        saveCameraAdjust(packageId, next);
        return next;
      });
      setVideoViewResetNonce((n) => n + 1);
    },
    [packageId],
  );

  const resetCameraAdjust = React.useCallback(() => {
    const next = cloneCameraAdjust(DEFAULT_CAMERA_ADJUST);
    ignoreCameraInteractionUntilRef.current = Date.now() + 500;
    setCameraAdjust(next);
    saveCameraAdjust(packageId, next);
    setShowVideoReference(true);
    setCourtRotationSteps(0);
    setVideoViewResetNonce((n) => n + 1);
  }, [packageId]);

  const clearAllCalibration = React.useCallback(() => {
    const next = cloneCameraAdjust(DEFAULT_CAMERA_ADJUST);
    ignoreCameraInteractionUntilRef.current = Date.now() + 500;
    clearCameraAdjust(packageId);
    setCameraAdjust(next);
    setCourtFitActive(false);
    setCourtFitClicks([]);
    clearCourtFitClicksStorage(packageId);
    setCourtFitErrorPx(null);
    setCourtFitResiduals([]);
    setCourtFitSolveStatus(null);
    setCourtHomography(null);
    setCourtHomographyStatus(null);
    setShowVideoReference(true);
    setCourtRotationSteps(0);
    setVideoViewResetNonce((n) => n + 1);
  }, [packageId]);

  const nextCourtFitLandmark =
    COURT_FIT_LANDMARKS.find(
      (landmark) => !courtFitClicks.some((click) => click.pointId === landmark.id),
    ) ?? null;

  const handleCourtFitClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!courtFitActive || !videoContentRect || !nextCourtFitLandmark) return;
      event.preventDefault();
      event.stopPropagation();
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      setCourtFitClicks((cur) => {
        const next = normalizeCourtFitClicks([
          ...cur.filter((click) => click.pointId !== nextCourtFitLandmark.id),
          { pointId: nextCourtFitLandmark.id, x, y },
        ]);
        saveCourtFitClicks(packageId, next);
        return next;
      });
      setCourtFitErrorPx(null);
      setCourtFitResiduals([]);
      setCourtFitSolveStatus(null);
      setCourtHomography(null);
      setCourtHomographyStatus(null);
    },
    [courtFitActive, nextCourtFitLandmark, packageId, videoContentRect],
  );

  const undoCourtFitClick = React.useCallback(() => {
    setCourtFitClicks((cur) => {
      const next = cur.slice(0, -1);
      saveCourtFitClicks(packageId, next);
      return next;
    });
    setCourtFitErrorPx(null);
    setCourtFitResiduals([]);
    setCourtFitSolveStatus(null);
    setCourtHomography(null);
    setCourtHomographyStatus(null);
  }, [packageId]);

  const clearCourtFitClicks = React.useCallback(() => {
    setCourtFitClicks([]);
    clearCourtFitClicksStorage(packageId);
    setCourtFitErrorPx(null);
    setCourtFitResiduals([]);
    setCourtFitSolveStatus(null);
    setCourtHomography(null);
    setCourtHomographyStatus(null);
  }, [packageId]);

  const removeCourtFitClick = React.useCallback(
    (pointId: CourtFitPointId, activate: boolean) => {
      setCourtFitClicks((cur) => {
        const next = cur.filter((click) => click.pointId !== pointId);
        saveCourtFitClicks(packageId, next);
        return next;
      });
      setCourtFitErrorPx(null);
      setCourtFitResiduals([]);
      setCourtFitSolveStatus(null);
      setCourtHomography(null);
      setCourtHomographyStatus(null);
      if (activate) {
        setCourtFitActive(true);
        setShowVideoReference(true);
      }
    },
    [packageId],
  );

  const applyCourtHomography = React.useCallback(() => {
    const solved = fitCourtHomography(courtFitClicks);
    if (!solved) {
      const equationCount = planarEquationCount(courtFitClicks);
      setCourtHomography(null);
      setCourtHomographyStatus(
        `Planar fit skipped: need 8 constraints (${equationCount} available).`,
      );
      return;
    }
    setCourtHomography(solved.homography);
    setCourtHomographyStatus(
      `Planar preview: ${solved.used} clicks / ${solved.equations} constraints · avg ${solved.errorPx.toFixed(1)}px`,
    );
  }, [courtFitClicks]);

  const handleShotSpaceCameraInteraction = React.useCallback(() => {
    if (videoControlsInteractionActiveRef.current) return;
    if (Date.now() < ignoreCameraInteractionUntilRef.current) return;
    setShowVideoReference((current) => (current ? false : current));
  }, []);

  const handleVideoControlsInteractionChange = React.useCallback((active: boolean) => {
    if (videoControlsInteractionActiveRef.current === active) return;
    videoControlsInteractionActiveRef.current = active;
    setVideoControlsInteractionActive(active);
    if (active) {
      ignoreCameraInteractionUntilRef.current = Date.now() + 1200;
    }
  }, []);

  const handleVideoCourtProjection = React.useCallback(
    (projection: ShotUniverseVideoCourtProjection | null) => {
      setVideoCourtProjection((current) => {
        if (!current || !projection) return current === projection ? current : projection;
        if (
          current.outer === projection.outer &&
          current.lines.length === projection.lines.length &&
          current.lines.every((line, index) => line === projection.lines[index])
        ) {
          return current;
        }
        return projection;
      });
    },
    [],
  );

  const resolvedRow =
    rows.find((candidate) => candidate.rallyIndex === selectedRallyIndex) ??
    row ??
    rows[0] ??
    null;
  const useIncomingShotNumber = resolvedRow?.rallyIndex === row?.rallyIndex;
  const resolvedShotNumber =
    manualShotNumber ??
    (useIncomingShotNumber ? shotNumber : null) ??
    resolvedRow?.shotNumberOverlays?.[0]?.shotNumber ??
    resolvedRow?.breakShotNumber ??
    1;

  const context = React.useMemo(
    () =>
      resolvedRow && !liveContextCleared
        ? buildShotUniverseContext({
            row: resolvedRow,
            rows,
            rallyIndex: resolvedRow.rallyIndex,
            shotNumber: resolvedShotNumber,
            insightsJson,
            cvJson,
            insightsToCvPlayerIndex,
          })
        : null,
    [
      resolvedRow,
      liveContextCleared,
      rows,
      resolvedShotNumber,
      insightsJson,
      cvJson,
      insightsToCvPlayerIndex,
    ],
  );

  const movementContext = React.useMemo(() => {
    if (!resolvedRow || liveContextCleared) return context;
    const contactedOverlay = contactedShotOverlayForTime(resolvedRow, debugVideoTimeSec);
    const contactedShotNumber = contactedOverlay?.shotNumber ?? resolvedShotNumber;
    return buildShotUniverseContext({
      row: resolvedRow,
      rows,
      rallyIndex: resolvedRow.rallyIndex,
      shotNumber: contactedShotNumber,
      insightsJson,
      cvJson,
      insightsToCvPlayerIndex,
    });
  }, [
    resolvedRow,
    liveContextCleared,
    context,
    debugVideoTimeSec,
    resolvedShotNumber,
    rows,
    insightsJson,
    cvJson,
    insightsToCvPlayerIndex,
  ]);

  const selectedOverlay = context?.selectedShot ?? null;
  const selectedOverlayVideoTimeSec =
    selectedOverlay && Number.isFinite(getShotContactMsRel(resolvedRow, selectedOverlay))
      ? Math.max(0, getShotContactMsRel(resolvedRow, selectedOverlay) / 1000)
      : 0;
  const selectedShotLoopSeed = React.useMemo(() => {
    if (!resolvedRow || !selectedOverlay) return null;
    const overlays = getSortedShotOverlays(resolvedRow);
    const selectedIndex = overlays.findIndex(
      (overlay) => overlay.shotNumber === selectedOverlay.shotNumber,
    );
    if (selectedIndex < 0) return null;
    const selectedMs = getShotContactMsRel(resolvedRow, overlays[selectedIndex]);
    if (!Number.isFinite(selectedMs)) return null;
    const nextOverlay = overlays[selectedIndex + 1] ?? null;
    const nextMs = nextOverlay ? getShotContactMsRel(resolvedRow, nextOverlay) : NaN;
    const segmentStartMs = Number(resolvedRow.segmentStartMs ?? resolvedRow.startMs ?? 0);
    const segmentEndMs = Number(
      resolvedRow.segmentEndMs ?? resolvedRow.endMs ?? segmentStartMs + 1000,
    );
    const durationSec = Math.max(0.5, (segmentEndMs - segmentStartMs) / 1000);
    const selectedSec = selectedMs / 1000;
    const aSec = Math.max(0, selectedSec - 0.18);
    const rawEndSec = Number.isFinite(nextMs)
      ? nextMs / 1000 + 0.65
      : durationSec;
    return {
      enabled: true,
      timeSec: aSec,
      aSec,
      bSec: Math.max(aSec + 0.3, Math.min(durationSec, rawEndSec)),
      nonce: (resolvedRow.rallyIndex + 1) * 100000 + selectedOverlay.shotNumber,
    };
  }, [resolvedRow, selectedOverlay]);
  const currentBookmark = React.useMemo(
    () =>
      resolvedRow
        ? bookmarks.find(
            (bookmark) =>
              bookmark.rallyIndex === resolvedRow.rallyIndex &&
              bookmark.shotNumber === resolvedShotNumber,
          ) ?? null
        : null,
    [bookmarks, resolvedRow, resolvedShotNumber],
  );
  const bookmarkPlayerId =
    selectedOverlay && Number.isFinite(Number((selectedOverlay as any).playerId))
      ? Number((selectedOverlay as any).playerId)
      : null;
  const bookmarkPlayerName =
    bookmarkPlayerId == null ? null : `Player ${bookmarkPlayerId}`;
  const bookmarkContextLabel = `Shot ${resolvedShotNumber} · ${
    bookmarkPlayerId == null ? "Player unknown" : `Player ${bookmarkPlayerId}`
  }`;
  const openBookmarkEditor = React.useCallback(() => {
    setBookmarkDescriptionDraft(currentBookmark?.description ?? "");
    setBookmarkEditorOpen(true);
  }, [currentBookmark?.description]);
  const saveCurrentBookmark = React.useCallback(() => {
    if (!resolvedRow) return;
    upsertShotBookmark({
      packageId,
      rallyIndex: resolvedRow.rallyIndex,
      shotNumber: resolvedShotNumber,
      timeSec: selectedOverlayVideoTimeSec || null,
      playerId: bookmarkPlayerId,
      playerName: bookmarkPlayerName,
      description: bookmarkDescriptionDraft,
      lab:
        viewMode === "court3d" &&
        (labSelection.target || labSelection.targets?.length || labSelection.pathLabel)
          ? {
              target: labSelection.target,
              targets: labSelection.targets ?? (labSelection.target ? [labSelection.target] : null),
              pathLabel: labSelection.pathLabel,
            }
          : null,
    });
    setBookmarkEditorOpen(false);
  }, [
    bookmarkDescriptionDraft,
    bookmarkPlayerId,
    bookmarkPlayerName,
    labSelection.pathLabel,
    labSelection.target,
    labSelection.targets,
    packageId,
    resolvedRow,
    resolvedShotNumber,
    selectedOverlayVideoTimeSec,
    viewMode,
  ]);
  const lingeringActualOpacity = lingeringActualShot
    ? LINGERING_SHOT_MAX_OPACITY *
      Math.min(
        Math.max(
          0,
          Math.min(
            1,
            (lingeringActualShot.endSec - debugVideoTimeSec) /
              Math.max(0.12, lingeringActualShot.endSec - lingeringActualShot.startSec),
          ),
        ),
        Math.max(
          0,
          1 - (Date.now() - lingeringActualShot.startedAtMs) / LINGERING_SHOT_FADE_MS,
        ),
      )
    : 0;
  const seedTimeSec =
    selectedOverlayVideoTimeSec > 0 ? Math.max(0, selectedOverlayVideoTimeSec - 1) : 0;
  const seedExactTimeSec = selectedShotLoopSeed?.timeSec ?? selectedOverlayVideoTimeSec;
  const shotAbsMs =
    resolvedRow &&
    selectedOverlay &&
    Number.isFinite(getShotContactMsRel(resolvedRow, selectedOverlay))
      ? (resolvedRow.segmentStartMs ?? resolvedRow.startMs ?? 0) +
        getShotContactMsRel(resolvedRow, selectedOverlay)
      : null;

  const loadCvCourtFitClicks = React.useCallback(() => {
    if (!videoContentRect) {
      setCourtFitSolveStatus("CV load skipped: video bounds are not ready yet.");
      return;
    }
    const clicks = buildCourtFitClicksFromCv({
      cvJson,
      shotAbsMs,
      videoWidth: videoContentRect.width,
      videoHeight: videoContentRect.height,
    });
    if (!clicks.length) {
      setCourtFitSolveStatus("CV load skipped: no camera.segment court_points found.");
      return;
    }
    setCourtFitClicks(clicks);
    saveCourtFitClicks(packageId, clicks);
    setCourtFitActive(false);
    setShowVideoReference(true);
    setCourtFitErrorPx(null);
    setCourtFitResiduals([]);
    setCourtFitSolveStatus(`Loaded ${clicks.length} PBV cv.json court points.`);
    const solved = fitCourtHomography(clicks);
    if (solved) {
      setCourtHomography(solved.homography);
      setCourtHomographyStatus(
        `Planar preview from cv.json: ${solved.used} clicks / ${solved.equations} constraints · avg ${solved.errorPx.toFixed(1)}px`,
      );
    } else {
      setCourtHomography(null);
      setCourtHomographyStatus("Planar preview skipped: CV points were not enough to solve.");
    }
  }, [cvJson, packageId, shotAbsMs, videoContentRect]);

  const applyCourtFit = React.useCallback(() => {
    if (!videoContentRect) {
      setCourtFitSolveStatus("Solve skipped: video bounds are not ready yet.");
      return;
    }
    const solved = solveCourtFit({
      cvJson,
      shotAbsMs,
      currentAdjust: cameraAdjust,
      clicks: courtFitClicks,
      videoWidth: videoContentRect.width,
      videoHeight: videoContentRect.height,
      bleedPx: SHOT_SPACE_VIEW_BLEED_PX,
    });
    if (!solved) {
      setCourtFitSolveStatus(
        `Solve skipped: need at least 4 usable points (${courtFitClicks.length} clicked).`,
      );
      return;
    }
    const delta = cameraAdjustDeltaMagnitude(cameraAdjust, solved.adjust);
    ignoreCameraInteractionUntilRef.current = Date.now() + 500;
    setCameraAdjust(solved.adjust);
    saveCameraAdjust(packageId, solved.adjust);
    setCourtFitErrorPx(solved.errorPx);
    setCourtFitResiduals(solved.residuals);
    setCourtFitSolveStatus(
      delta < 0.05
        ? `Solve ran: no better fit found · avg ${solved.errorPx.toFixed(1)}px`
        : `Solve applied: changed ${delta.toFixed(2)} · avg ${solved.errorPx.toFixed(1)}px`,
    );
    setVideoViewResetNonce((n) => n + 1);
  }, [cameraAdjust, courtFitClicks, cvJson, packageId, shotAbsMs, videoContentRect]);

  const applyPlanarToCamera = React.useCallback(() => {
    if (!courtHomography || !videoContentRect) {
      setCourtHomographyStatus("Apply skipped: run Planar preview first.");
      return;
    }
    const solved = solveCameraToHomography({
      homography: courtHomography,
      cvJson,
      shotAbsMs,
      currentAdjust: cameraAdjust,
      videoWidth: videoContentRect.width,
      videoHeight: videoContentRect.height,
      bleedPx: SHOT_SPACE_VIEW_BLEED_PX,
    });
    if (!solved) {
      setCourtHomographyStatus("Apply skipped: could not derive a 3D camera from the planar fit.");
      return;
    }
    const next = solved.adjust;
    const delta = cameraAdjustDeltaMagnitude(cameraAdjust, next);
    ignoreCameraInteractionUntilRef.current = Date.now() + 500;
    setCameraAdjust(next);
    saveCameraAdjust(packageId, next);
    setShowVideoReference(true);
    setCourtFitActive(false);
    setVideoViewResetNonce((n) => n + 1);
    setCourtHomographyStatus(
      delta < 0.05
        ? `Planar fit checked: no better camera found · sampled ${solved.initialErrorPx.toFixed(1)}px → ${solved.errorPx.toFixed(1)}px`
        : `Planar fit applied to the 3D camera · changed ${delta.toFixed(2)} · sampled ${solved.initialErrorPx.toFixed(1)}px → ${solved.errorPx.toFixed(1)}px`,
    );
  }, [cameraAdjust, courtHomography, cvJson, packageId, shotAbsMs, videoContentRect]);

  const resolvedRallyShotCount =
    resolvedRow?.numShots ??
    resolvedRow?.shotNumberOverlays?.length ??
    null;
  const liveProjectionStats = React.useMemo(
    () => liveProjectionErrorStats(courtHomography, videoCourtProjection),
    [courtHomography, videoCourtProjection],
  );
  const useProjectedVideoCourtLines = Boolean(videoCourtProjection);

  const debugTiming = React.useMemo(() => {
    if (!SHOW_TIMING_DEBUG) return null;

    const overlay = selectedOverlay;
    const nearest =
      resolvedRow && Number.isFinite(debugVideoTimeSec)
        ? nearestShotOverlayForTime(resolvedRow, debugVideoTimeSec)
        : null;
    const midpoint =
      resolvedRow && Number.isFinite(debugVideoTimeSec)
        ? midpointShotOverlayForTime(
            resolvedRow,
            debugVideoTimeSec,
            normalizedShotSwitchTiming,
          )
        : null;
    const contacted =
      resolvedRow && Number.isFinite(debugVideoTimeSec)
        ? contactedShotOverlayForTime(resolvedRow, debugVideoTimeSec)
        : null;
    const sortedOverlays = getSortedShotOverlays(resolvedRow);
    const midpointIndex = midpoint
      ? sortedOverlays.findIndex((candidate) => candidate.shotNumber === midpoint.shotNumber)
      : -1;
    const previousOverlay =
      midpointIndex > 0 ? sortedOverlays[midpointIndex - 1] : null;
    const nextOverlay =
      midpointIndex >= 0 && midpointIndex + 1 < sortedOverlays.length
        ? sortedOverlays[midpointIndex + 1]
        : null;
    const midpointSwitchMs =
      midpointIndex >= 0
        ? getSwitchMsForOverlayIndex(
            resolvedRow,
            sortedOverlays,
            midpointIndex,
            normalizedShotSwitchTiming,
          )
        : NaN;
    const nextSwitchMs =
      midpointIndex >= 0 && midpointIndex + 1 < sortedOverlays.length
        ? getSwitchMsForOverlayIndex(
            resolvedRow,
            sortedOverlays,
            midpointIndex + 1,
            normalizedShotSwitchTiming,
          )
        : NaN;
    const shotWindow =
      midpointIndex >= 0
        ? sortedOverlays
            .slice(Math.max(0, midpointIndex - 2), midpointIndex + 4)
            .map((candidate) => {
              const index = sortedOverlays.findIndex(
                (overlayCandidate) =>
                  overlayCandidate.shotNumber === candidate.shotNumber,
              );
              const contactMs = getShotContactMsRel(resolvedRow, candidate);
              const switchMs = getSwitchMsForOverlayIndex(
                resolvedRow,
                sortedOverlays,
                index,
                normalizedShotSwitchTiming,
              );
              return {
                shotNumber: candidate.shotNumber,
                contactMs: Number.isFinite(contactMs) ? contactMs : null,
                switchMs: Number.isFinite(switchMs) ? switchMs : null,
              };
            })
        : [];
    const baseMs = getRowOverlayBaseMs(resolvedRow);
    const segmentStartMs = Number(resolvedRow?.segmentStartMs ?? resolvedRow?.startMs);
    const segmentEndMs = Number(resolvedRow?.segmentEndMs ?? resolvedRow?.endMs);
    const rawAtMsRel = Number(overlay?.atMsRel);
    const rawTrajectoryStartMs = Number(
      overlay?.trajectoryStart?.ms ??
        overlay?.rawShot?.resulting_ball_movement?.trajectory?.start?.ms,
    );
    const rawShotStartMs = Number(overlay?.rawShot?.start_ms);
    const normalizedSelectedMs = getShotContactMsRel(resolvedRow, overlay);
    const normalizedNearestMs = getShotContactMsRel(resolvedRow, nearest);
    const normalizedMidpointMs = getShotContactMsRel(resolvedRow, midpoint);
    return {
      videoSec: debugVideoTimeSec,
      selectedShot: overlay?.shotNumber ?? null,
      playbackShot: debugPlaybackShotNumber,
      nearestShot: nearest?.shotNumber ?? null,
      midpointShot: midpoint?.shotNumber ?? null,
      contactedShot: contacted?.shotNumber ?? null,
      movementShot: movementContext?.shotNumber ?? null,
      movementNextShot:
        movementContext?.movementCues.length && movementContext.shotNumber
          ? movementContext.shotNumber + 1
          : null,
      movementCueCount: movementContext?.movementCues.length ?? 0,
      movementPlayerCount: movementContext?.playerPositions.length ?? 0,
      previousShot: previousOverlay?.shotNumber ?? null,
      nextShot: nextOverlay?.shotNumber ?? null,
      baseMs,
      segmentStartMs,
      segmentEndMs,
      rawAtMsRel: Number.isFinite(rawAtMsRel) ? rawAtMsRel : null,
      rawTrajectoryStartMs: Number.isFinite(rawTrajectoryStartMs)
        ? rawTrajectoryStartMs
        : null,
      rawShotStartMs: Number.isFinite(rawShotStartMs) ? rawShotStartMs : null,
      normalizedSelectedMs: Number.isFinite(normalizedSelectedMs)
        ? normalizedSelectedMs
        : null,
      normalizedNearestMs: Number.isFinite(normalizedNearestMs)
        ? normalizedNearestMs
        : null,
      normalizedMidpointMs: Number.isFinite(normalizedMidpointMs)
        ? normalizedMidpointMs
        : null,
      previousContactMs: previousOverlay
        ? getShotContactMsRel(resolvedRow, previousOverlay)
        : null,
      midpointContactMs: midpoint ? getShotContactMsRel(resolvedRow, midpoint) : null,
      nextContactMs: nextOverlay ? getShotContactMsRel(resolvedRow, nextOverlay) : null,
      midpointSwitchMs: Number.isFinite(midpointSwitchMs) ? midpointSwitchMs : null,
      nextSwitchMs: Number.isFinite(nextSwitchMs) ? nextSwitchMs : null,
      shotWindow,
      seedSec: playbackSeed?.timeSec ?? null,
      seedPaused: playbackSeed?.paused ?? null,
      lastMidpointUpdate: debugLastMidpointUpdate,
      lastLabelUpdate: debugLastLabelUpdate,
      suppressMsLeft: Math.max(0, suppressLiveShotUpdatesUntilRef.current - Date.now()),
      suppressBelow: suppressLiveShotUpdatesBelowRef.current,
    };
  }, [
    debugLastLabelUpdate,
    debugLastMidpointUpdate,
    debugPlaybackShotNumber,
    debugVideoTimeSec,
    playbackSeed,
    resolvedRow,
    selectedOverlay,
    normalizedShotSwitchTiming,
    movementContext,
  ]);

  const contactOverlays = React.useMemo(
    () =>
      [...(resolvedRow?.shotNumberOverlays ?? [])]
        .filter((overlay) => Number.isFinite(getShotContactMsRel(resolvedRow, overlay)))
        .sort(
          (a, b) =>
            getShotContactMsRel(resolvedRow, a) - getShotContactMsRel(resolvedRow, b),
    ),
    [resolvedRow],
  );
  const opportunitySourceShotNumber = opportunityContext?.shotNumber ?? null;
  const opportunitySourceOverlay =
    opportunitySourceShotNumber == null
      ? null
      : contactOverlays.find(
          (overlay) => overlay.shotNumber === opportunitySourceShotNumber,
        ) ?? null;
  const opportunitySourceShotMsRel = getShotContactMsRel(
    resolvedRow,
    opportunitySourceOverlay,
  );
  const opportunitySourceShotTimeSec = Number.isFinite(opportunitySourceShotMsRel)
    ? Math.max(0, opportunitySourceShotMsRel / 1000)
    : null;

  React.useEffect(() => {
    if (!context) {
      previousShotSpaceContextRef.current = null;
      setLingeringActualShot(null);
      return;
    }

    const previous = previousShotSpaceContextRef.current;
    if (
      previous &&
      previous.rallyIndex === context.rallyIndex &&
      previous.shotNumber !== context.shotNumber &&
      previous.actualTrajectory.length >= 2
    ) {
      if (context.shotNumber <= previous.shotNumber) {
        setLingeringActualShot(null);
        previousShotSpaceContextRef.current = context;
        return;
      }
      const contactClearSec =
        selectedOverlayVideoTimeSec > debugVideoTimeSec + 0.02
          ? selectedOverlayVideoTimeSec
          : debugVideoTimeSec + LINGERING_SHOT_MIN_SEC;
      const visualEndSec = Math.max(
        debugVideoTimeSec + LINGERING_SHOT_MIN_SEC,
        Math.min(contactClearSec, debugVideoTimeSec + LINGERING_SHOT_MAX_SEC),
      );
      setLingeringActualShot({
        context: previous,
        startSec: debugVideoTimeSec,
        endSec: visualEndSec,
        startedAtMs: Date.now(),
      });
    } else if (!previous || previous.rallyIndex !== context.rallyIndex) {
      setLingeringActualShot(null);
    }

    previousShotSpaceContextRef.current = context;
  }, [
    context?.rallyIndex,
    context?.shotNumber,
    debugVideoTimeSec,
    selectedOverlayVideoTimeSec,
  ]);

  React.useEffect(() => {
    if (!lingeringActualShot) return;
    if (debugVideoTimeSec >= lingeringActualShot.endSec - 0.02) {
      setLingeringActualShot(null);
    }
  }, [debugVideoTimeSec, lingeringActualShot]);

  React.useEffect(() => {
    if (!lingeringActualShot) return;
    const remainingMs = Math.max(
      0,
      LINGERING_SHOT_FADE_MS - (Date.now() - lingeringActualShot.startedAtMs),
    );
    const timeout = window.setTimeout(() => {
      setLingeringActualShot((cur) =>
        cur?.startedAtMs === lingeringActualShot.startedAtMs ? null : cur,
      );
    }, remainingMs + 40);
    return () => window.clearTimeout(timeout);
  }, [lingeringActualShot]);

  const selectRally = React.useCallback((candidate: KitchenArrivalRow) => {
    const nextShotNumber = pickShotNumberForRally(candidate);
    const nextOverlay =
      candidate.shotNumberOverlays?.find(
        (overlay) => overlay.shotNumber === nextShotNumber,
      ) ?? null;
    const nextContactMsRel = getShotContactMsRel(candidate, nextOverlay);
    setSelectedRallyIndex(candidate.rallyIndex);
    setManualShotNumber(nextShotNumber);
    setLiveContextCleared(false);
    setVideoPauseSnapArmed(false);
    setContactPausedShotNumber(null);
    suppressLiveShotUpdatesUntilRef.current = Date.now() + 700;
    suppressLiveShotUpdatesBelowRef.current = nextShotNumber;
    setPlaybackSeed(
      Number.isFinite(nextContactMsRel)
        ? {
            timeSec: Math.max(0, nextContactMsRel / 1000),
            paused: true,
            nonce: Date.now(),
          }
        : null,
    );
    lastContactCheckMsRef.current = null;
    lastAutoPausedShotNumberRef.current = null;
    lastVideoSyncedShotNumberRef.current = nextShotNumber;
    setShowVideoReference(true);
    setCourtRotationSteps(0);
    setVideoViewResetNonce((n) => n + 1);
    setRallyReview(null);
    setRallyReviewError(null);
    setRallyReviewLoopSeed(null);
  }, []);

  const openBookmarkInLab = React.useCallback(
    (bookmark: ShotBookmark, mode: ViewMode) => {
      const nextRow = rows.find((candidate) => candidate.rallyIndex === bookmark.rallyIndex);
      if (!nextRow) return;
      setSelectedRallyIndex(nextRow.rallyIndex);
      setManualShotNumber(bookmark.shotNumber);
      setLiveContextCleared(false);
      setContactPausedShotNumber(null);
      setViewMode(mode);
      setShowVideoReference(true);
      setVideoPauseSnapArmed(false);
      setPlaybackSeed({
        timeSec: Math.max(0, bookmark.timeSec ?? 0),
        paused: true,
        nonce: Date.now(),
      });
      setRestoreLabSelection(
        mode === "court3d" && (bookmark.lab?.targets?.length || bookmark.lab?.target)
          ? {
              nonce: Date.now(),
              target: bookmark.lab.target,
              targets: bookmark.lab.targets ?? (bookmark.lab.target ? [bookmark.lab.target] : null),
              pathLabel: bookmark.lab.pathLabel ?? null,
            }
          : null,
      );
      setBookmarksOpen(false);
    },
    [rows],
  );

  React.useEffect(() => {
    if (!restoreBookmark?.nonce) return;
    openBookmarkInLab(restoreBookmark.bookmark, "court3d");
  }, [openBookmarkInLab, restoreBookmark?.bookmark, restoreBookmark?.nonce]);

  const goToAdjacentRally = React.useCallback(
    (dir: -1 | 1) => {
      if (!rows.length) return;
      const currentIndex = rows.findIndex(
        (candidate) => candidate.rallyIndex === resolvedRow?.rallyIndex,
      );
      const baseIndex = currentIndex >= 0 ? currentIndex : 0;
      const nextIndex = (baseIndex + dir + rows.length) % rows.length;
      const next = rows[nextIndex];
      if (!next || next.rallyIndex === resolvedRow?.rallyIndex) return;
      selectRally(next);
    },
    [resolvedRow?.rallyIndex, rows, selectRally],
  );

  const snapTimeTo3d = React.useCallback((timeSec: number, pauseVideo: boolean) => {
    const video = activeVideoRef.current?.current ?? null;
    if (!resolvedRow) {
      setViewMode("court3d");
      setShowVideoReference(true);
      return;
    }
    const nearest = nearestShotOverlayForTime(resolvedRow, timeSec);
    const nextShotNumber = nearest?.shotNumber ?? resolvedShotNumber;
    const shouldReseed =
      viewMode !== "court3d" ||
      manualShotNumber !== nextShotNumber ||
      !showVideoReference;

    if (pauseVideo && video) {
      suppressNextPauseSnapRef.current = true;
      video.pause();
      window.setTimeout(() => {
        suppressNextPauseSnapRef.current = false;
      }, 0);
    }
    if (nearest) {
      const nearestContactMsRel = getShotContactMsRel(resolvedRow, nearest);
      if (video && Number.isFinite(nearestContactMsRel)) {
        video.currentTime = Math.max(0, nearestContactMsRel / 1000);
      }
      if (Number.isFinite(nearestContactMsRel)) {
        setPlaybackSeed({
          timeSec: Math.max(0, nearestContactMsRel / 1000),
          paused: true,
          nonce: Date.now(),
        });
      }
      setManualShotNumber(nearest.shotNumber);
      setLiveContextCleared(false);
    }
    setViewMode("court3d");
    setShowVideoReference(true);
    if (shouldReseed) {
      setVideoViewResetNonce((n) => n + 1);
    }
  }, [manualShotNumber, resolvedRow, resolvedShotNumber, showVideoReference, viewMode]);

  const handlePlaybackShotNumberChange = React.useCallback(
    (currentShotNumber: number | null) => {
      const video = activeVideoRef.current?.current ?? null;
      setDebugPlaybackShotNumber(currentShotNumber);
      setDebugLastLabelUpdate({
        shotNumber: currentShotNumber,
        videoSec:
          video && Number.isFinite(video.currentTime) ? video.currentTime : debugVideoTimeSec,
      });
    },
    [debugVideoTimeSec],
  );

  const snapToShotContact = React.useCallback(
    (overlay: ShotNumberOverlay, paused: boolean) => {
      const video = activeVideoRef.current?.current ?? null;
      const shotMs = getShotContactMsRel(resolvedRow, overlay);
      if (!Number.isFinite(shotMs)) return;

      const timeSec = Math.max(0, shotMs / 1000);
      suppressNextPauseSnapRef.current = true;
      if (video) {
        try {
          video.pause();
          video.currentTime = timeSec;
          if (!paused) {
            video.play().catch(() => {});
          }
        } catch {}
      }
      window.setTimeout(() => {
        suppressNextPauseSnapRef.current = false;
      }, 0);

      lastContactCheckMsRef.current = shotMs;
      lastAutoPausedShotNumberRef.current = overlay.shotNumber;
      lastVideoSyncedShotNumberRef.current = overlay.shotNumber;
      setContactPausedShotNumber(paused ? overlay.shotNumber : null);
      setManualShotNumber(overlay.shotNumber);
      setLiveContextCleared(false);
      setPlaybackSeed({
        timeSec,
        paused,
        nonce: Date.now(),
      });
    },
    [resolvedRow],
  );

  React.useEffect(() => {
    lastContactCheckMsRef.current = null;
    lastAutoPausedShotNumberRef.current = null;
    lastVideoSyncedShotNumberRef.current = null;
    setContactPausedShotNumber(null);
  }, [resolvedRow?.rallyIndex]);

  React.useEffect(() => {
    if (!resolvedRow) return;

    let raf = 0;
    const tick = () => {
      const video = activeVideoRef.current?.current ?? null;
      if (video && Number.isFinite(video.currentTime)) {
        setIsVideoPlaying((cur) => (cur === !video.paused ? cur : !video.paused));
        setDebugVideoTimeSec((cur) =>
          Math.abs(cur - video.currentTime) > 0.04 ? video.currentTime : cur,
        );
        const midpoint = midpointShotOverlayForTime(
          resolvedRow,
          video.currentTime,
          normalizedShotSwitchTiming,
        );
        const midpointShotNumber = midpoint?.shotNumber ?? null;
        const suppressing = Date.now() < suppressLiveShotUpdatesUntilRef.current;
        const minAccepted = suppressLiveShotUpdatesBelowRef.current;
        const staleDuringSwitch =
          suppressing &&
          midpointShotNumber != null &&
          minAccepted != null &&
          midpointShotNumber < minAccepted;
        const pausedBeforeSelectedShot =
          video.paused &&
          manualShotNumber != null &&
          midpointShotNumber != null &&
          midpointShotNumber < manualShotNumber;
        if (
          midpointShotNumber != null &&
          !staleDuringSwitch &&
          !pausedBeforeSelectedShot &&
          lastVideoSyncedShotNumberRef.current !== midpointShotNumber
        ) {
          lastVideoSyncedShotNumberRef.current = midpointShotNumber;
          suppressLiveShotUpdatesBelowRef.current = null;
          setLiveContextCleared(false);
          setDebugLastMidpointUpdate({
            shotNumber: midpointShotNumber,
            videoSec: video.currentTime,
          });
          setManualShotNumber((cur) =>
            cur === midpointShotNumber ? cur : midpointShotNumber,
          );
        }
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [manualShotNumber, resolvedRow, shotSwitchTiming]);

  React.useEffect(() => {
    if (!pauseAtShotContacts || !contactOverlays.length) return;

    let raf = 0;
    const tick = () => {
      const video = activeVideoRef.current?.current ?? null;
      if (video) {
        const currentMs = video.currentTime * 1000;
        const previousMs = lastContactCheckMsRef.current;

        if (previousMs == null || currentMs < previousMs) {
          lastContactCheckMsRef.current = currentMs;
        } else if (!video.paused) {
          const crossed = contactOverlays.find((overlay) => {
            const atMs = getShotContactMsRel(resolvedRow, overlay);
            return (
              atMs > previousMs + 15 &&
              atMs <= currentMs + 35 &&
              overlay.shotNumber !== lastAutoPausedShotNumberRef.current
            );
          });

          if (crossed) {
            suppressNextPauseSnapRef.current = true;
            lastAutoPausedShotNumberRef.current = crossed.shotNumber;
            const crossedMs = getShotContactMsRel(resolvedRow, crossed);
            lastContactCheckMsRef.current = crossedMs;
            const stoppedTimeSec = Number.isFinite(video.currentTime)
              ? video.currentTime
              : Math.max(0, crossedMs / 1000);
            try {
              video.pause();
            } catch {}
            setContactPausedShotNumber(crossed.shotNumber);
            setManualShotNumber(crossed.shotNumber);
            setLiveContextCleared(false);
            setPlaybackSeed({
              timeSec: Math.max(0, stoppedTimeSec),
              paused: true,
              nonce: Date.now(),
            });
            window.setTimeout(() => {
              suppressNextPauseSnapRef.current = false;
            }, 0);
          } else {
            lastContactCheckMsRef.current = currentMs;
          }
        }
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [contactOverlays, pauseAtShotContacts, resolvedRow]);

  const playToNextShotContact = React.useCallback(() => {
    const video = activeVideoRef.current?.current ?? null;
    if (!contactOverlays.length) return;

    const currentMs =
      video && Number.isFinite(video.currentTime) ? video.currentTime * 1000 : 0;
    const currentPausedOverlay =
      contactPausedShotNumber != null
        ? contactOverlays.find(
            (overlay) => overlay.shotNumber === contactPausedShotNumber,
          ) ?? null
        : null;
    const startMs =
      currentPausedOverlay &&
      Number.isFinite(getShotContactMsRel(resolvedRow, currentPausedOverlay))
        ? getShotContactMsRel(resolvedRow, currentPausedOverlay)
        : currentMs;
    const nextOverlay =
      contactOverlays.find(
        (overlay) => getShotContactMsRel(resolvedRow, overlay) > startMs + 40,
      ) ??
      null;

    if (!nextOverlay) {
      const firstOverlay = contactOverlays[0];
      if (firstOverlay) {
        snapToShotContact(firstOverlay, true);
      }
      return;
    }

    lastContactCheckMsRef.current = Math.max(0, startMs + 20);
    lastAutoPausedShotNumberRef.current = contactPausedShotNumber;
    setContactPausedShotNumber(null);

    setPlaybackSeed({
      timeSec: Math.max(0, (startMs + 120) / 1000),
      paused: false,
      nonce: Date.now(),
    });
  }, [contactOverlays, contactPausedShotNumber, resolvedRow, snapToShotContact]);

  const toggleVideoPlayback = React.useCallback(() => {
    const video = activeVideoRef.current?.current ?? null;
    setViewMode("court3d");
    setShowVideoReference(true);
    if (!video) {
      setPlaybackSeed({
        timeSec: Math.max(0, debugVideoTimeSec || seedExactTimeSec || 0),
        paused: isVideoPlaying,
        nonce: Date.now(),
      });
      setIsVideoPlaying((value) => !value);
      return;
    }

    if (video.paused) {
      const currentMs = Number.isFinite(video.currentTime) ? video.currentTime * 1000 : 0;
      lastContactCheckMsRef.current = currentMs;
      lastAutoPausedShotNumberRef.current = contactPausedShotNumber;
      setContactPausedShotNumber(null);
      setPlaybackSeed({
        timeSec: Math.max(0, video.currentTime),
        paused: false,
        nonce: Date.now(),
      });
      try {
        video.muted = true;
        video.defaultMuted = true;
        video.volume = 0;
      } catch {}
      video.play().catch(() => {});
      setIsVideoPlaying(true);
      return;
    }

    try {
      video.pause();
    } catch {}
    setPlaybackSeed({
      timeSec: Math.max(0, video.currentTime),
      paused: true,
      nonce: Date.now(),
    });
    setIsVideoPlaying(false);
  }, [contactPausedShotNumber, debugVideoTimeSec, isVideoPlaying, seedExactTimeSec]);

  const goToPreviousShotContact = React.useCallback(() => {
    const video = activeVideoRef.current?.current ?? null;
    if (!contactOverlays.length) return;

    const currentPausedOverlay =
      contactPausedShotNumber != null
        ? contactOverlays.find(
            (overlay) => overlay.shotNumber === contactPausedShotNumber,
          ) ?? null
        : null;
    const currentMs =
      currentPausedOverlay &&
      Number.isFinite(getShotContactMsRel(resolvedRow, currentPausedOverlay))
        ? getShotContactMsRel(resolvedRow, currentPausedOverlay)
        : video && Number.isFinite(video.currentTime)
          ? video.currentTime * 1000
          : 0;

    const previousOverlay =
      [...contactOverlays]
        .reverse()
        .find((overlay) => getShotContactMsRel(resolvedRow, overlay) < currentMs - 40) ??
      contactOverlays[contactOverlays.length - 1] ??
      null;

    if (previousOverlay) {
      snapToShotContact(previousOverlay, true);
    }
  }, [contactOverlays, contactPausedShotNumber, resolvedRow, snapToShotContact]);

  const goToNextShotContact = React.useCallback(() => {
    const video = activeVideoRef.current?.current ?? null;
    if (!contactOverlays.length) return;

    const currentPausedOverlay =
      contactPausedShotNumber != null
        ? contactOverlays.find(
            (overlay) => overlay.shotNumber === contactPausedShotNumber,
          ) ?? null
        : null;
    const currentMs =
      currentPausedOverlay &&
      Number.isFinite(getShotContactMsRel(resolvedRow, currentPausedOverlay))
        ? getShotContactMsRel(resolvedRow, currentPausedOverlay)
        : video && Number.isFinite(video.currentTime)
          ? video.currentTime * 1000
          : 0;

    const nextOverlay =
      contactOverlays.find(
        (overlay) => getShotContactMsRel(resolvedRow, overlay) > currentMs + 40,
      ) ??
      contactOverlays[0] ??
      null;

    if (nextOverlay) {
      snapToShotContact(nextOverlay, true);
    }
  }, [contactOverlays, contactPausedShotNumber, resolvedRow, snapToShotContact]);

  const handleVideoPlaybackPause = React.useCallback(
    (currentTimeSec: number) => {
      setIsVideoPlaying(false);
      if (suppressNextPauseSnapRef.current) {
        suppressNextPauseSnapRef.current = false;
        return;
      }
      if (viewMode !== "video" || !videoPauseSnapArmed) return;
      setVideoPauseSnapArmed(false);
      snapTimeTo3d(currentTimeSec, false);
    },
    [snapTimeTo3d, videoPauseSnapArmed, viewMode],
  );

  const handleVideoPlaybackPlay = React.useCallback(() => {
    setIsVideoPlaying(true);
    if (viewMode === "video") setVideoPauseSnapArmed(true);
  }, [viewMode]);

  const handleVideoRef = React.useCallback(
    (ref: React.RefObject<HTMLVideoElement | null>) => {
      activeVideoRef.current = ref;
      setVideoRefNonce((n) => n + 1);
    },
    [],
  );

  const runRallyReviewAgent = React.useCallback(async () => {
    if (!resolvedRow) return;
    setRallyReviewLoading(true);
    setRallyReviewError(null);
    try {
      const response = await fetch("/api/ai/rally-review", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          packageId,
          rallyIndex: resolvedRow.rallyIndex,
          shotNumber: context?.shotNumber ?? resolvedShotNumber,
        }),
      });
      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(`Rally review failed (${response.status}): ${text}`);
      }
      const result = (await response.json()) as RallyReviewWithPipeline;
      setRallyReview(result);
      setManualShotNumber(result.primaryMoment.shotNumber);
      setLiveContextCleared(false);
      setViewMode("court3d");
      setShowVideoReference(true);

      const segmentStartMs = Number(resolvedRow.segmentStartMs ?? resolvedRow.startMs ?? 0);
      const segmentEndMs = Number(
        resolvedRow.segmentEndMs ?? resolvedRow.endMs ?? segmentStartMs + 1000,
      );
      const loopDurationSec = Math.max(0.5, (segmentEndMs - segmentStartMs) / 1000);
      const aSec = Math.max(
        0,
        (Number(result.suggestedLoop.startMs) - segmentStartMs) / 1000,
      );
      const bSec = Math.max(
        aSec + 0.25,
        Math.min(
          loopDurationSec,
          (Number(result.suggestedLoop.endMs) - segmentStartMs) / 1000,
        ),
      );
      setRallyReviewLoopSeed({
        enabled: true,
        aSec,
        bSec,
        nonce: Date.now(),
      });
      setPlaybackSeed({
        timeSec: aSec,
        paused: true,
        nonce: Date.now(),
      });
    } catch (error: any) {
      setRallyReviewError(error?.message ?? String(error));
    } finally {
      setRallyReviewLoading(false);
    }
  }, [context?.shotNumber, packageId, resolvedRow, resolvedShotNumber]);

  const rallyReviewOverlayText = React.useCallback(
    (overlay: ShotNumberOverlay) => {
      if (!rallyReview) return undefined;
      return rallyReview.overlaySuggestions.find(
        (suggestion) => suggestion.shotNumber === overlay.shotNumber,
      )?.text;
    },
    [rallyReview],
  );

  const primaryReviewOverlayText =
    rallyReview?.overlaySuggestions.find(
      (suggestion) => suggestion.shotNumber === rallyReview.primaryMoment.shotNumber,
    )?.text ?? rallyReview?.primaryMoment.reason;

  React.useLayoutEffect(() => {
    if (!open || viewMode !== "court3d") return;

    let frameId = 0;
    const updateVideoContentRect = () => {
      const stage = stageRef.current;
      const video = activeVideoRef.current?.current ?? null;
      if (!stage || !video) {
        setVideoContentRect(null);
        return;
      }

      const stageRect = stage.getBoundingClientRect();
      const next = getContainedRect({
        containerWidth: stageRect.width,
        containerHeight: stageRect.height,
        contentWidth: video.videoWidth,
        contentHeight: video.videoHeight,
      });

      setVideoContentRect((cur) => {
        if (!next) return cur == null ? cur : null;
        if (
          cur &&
          Math.abs(cur.left - next.left) < 0.5 &&
          Math.abs(cur.top - next.top) < 0.5 &&
          Math.abs(cur.width - next.width) < 0.5 &&
          Math.abs(cur.height - next.height) < 0.5
        ) {
          return cur;
        }
        return next;
      });
    };

    const requestUpdate = () => {
      if (frameId) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(updateVideoContentRect);
    };

    requestUpdate();
    const stage = stageRef.current;
    const video = activeVideoRef.current?.current ?? null;
    const resizeObserver =
      stage && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(requestUpdate)
        : null;
    if (stage && resizeObserver) resizeObserver.observe(stage);
    video?.addEventListener("loadedmetadata", requestUpdate);
    video?.addEventListener("loadeddata", requestUpdate);
    window.addEventListener("resize", requestUpdate);

    return () => {
      if (frameId) cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      video?.removeEventListener("loadedmetadata", requestUpdate);
      video?.removeEventListener("loadeddata", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
    };
  }, [
    frame.height,
    frame.width,
    open,
    resolvedRow?.rallyIndex,
    showCalibration,
    showVideoReference,
    videoRefNonce,
    videoViewResetNonce,
    viewMode,
  ]);

  const videoNode = resolvedRow ? (
    <ActiveRallyVideo
      key={`${resolvedRow.rallyIndex}:${viewMode}`}
      aiOpenViaParent={false}
      packageId={packageId}
      row={resolvedRow}
      rows={rows}
      activeShotNumber={
        ENABLE_RALLY_REVIEW_AGENT && rallyReview
          ? rallyReview.primaryMoment.shotNumber
          : null
      }
      activeShotExtraText={
        ENABLE_RALLY_REVIEW_AGENT ? primaryReviewOverlayText : undefined
      }
      shotOverlayPersistentExtraText={
        ENABLE_RALLY_REVIEW_AGENT ? rallyReviewOverlayText : undefined
      }
      focusedFilter={null}
      insightsToCvPlayerIndex={insightsToCvPlayerIndex}
      renderPlayerIdentity={renderPlayerIdentity}
      aiContextLabel="Shot Target Lab"
      showCondensedTimeline={viewMode === "video"}
      fontColor="#ffffff"
      controlsPlacement="left"
      firefightsClips={firefightsClips}
      firefightRallySet={firefightRallySet}
      showRateControls={true}
      showUltraSlowRate={true}
      showTitle={viewMode === "video"}
      showScoreEtc={viewMode === "video"}
      showAi={true}
      showLoopScrubber={true}
      defaultLoopUiOpen={false}
      showPopoutButton={viewMode === "video"}
      isInFullPopup={false}
      pauseOtherVideosOnMount={true}
      cvJson={cvJson}
      stableVideoStage={viewMode === "court3d"}
      showTrajectoryShapePanel={viewMode === "court3d" && showMiniPlot}
      focusedPlayerId={null}
      shotOverlayManualHighlightLabel=""
      controlsPortalContainer={viewMode === "court3d" ? videoControlsPortalEl : null}
      loopControlsPortalContainer={
        viewMode === "court3d" ? videoLoopControlsPortalEl : null
      }
      requireLoopControlsPortal={viewMode === "court3d"}
      onControlsInteractionChange={handleVideoControlsInteractionChange}
      seekToActiveShotOnChange={false}
      onVideoRef={handleVideoRef}
      onPlaybackShotNumberChange={handlePlaybackShotNumberChange}
      onPlaybackPause={handleVideoPlaybackPause}
      onPlaybackPlay={handleVideoPlaybackPlay}
      onStepShot={(dir) => {
        if (dir < 0) {
          goToPreviousShotContact();
        } else {
          goToNextShotContact();
        }
      }}
      onStepRally={goToAdjacentRally}
      seedPlayback={{
        timeSec:
          playbackSeed?.timeSec ??
          (viewMode === "court3d" ? seedExactTimeSec : seedTimeSec),
        paused: playbackSeed?.paused ?? viewMode === "court3d",
        nonce:
          playbackSeed?.nonce ??
          ((resolvedRow.rallyIndex + 1) * 1000 + (viewMode === "court3d" ? 7 : 0)),
      }}
      seedLoopRegion={{
        enabled:
          (ENABLE_RALLY_REVIEW_AGENT ? rallyReviewLoopSeed?.enabled : null) ??
          selectedShotLoopSeed?.enabled ??
          false,
        aSec:
          (ENABLE_RALLY_REVIEW_AGENT ? rallyReviewLoopSeed?.aSec : null) ??
          selectedShotLoopSeed?.aSec ??
          0,
        bSec:
          (ENABLE_RALLY_REVIEW_AGENT ? rallyReviewLoopSeed?.bSec : null) ??
          selectedShotLoopSeed?.bSec ??
          Math.max(1, ((resolvedRow.endMs ?? 0) - (resolvedRow.startMs ?? 0)) / 1000),
        nonce:
          (ENABLE_RALLY_REVIEW_AGENT ? rallyReviewLoopSeed?.nonce : null) ??
          selectedShotLoopSeed?.nonce ??
          (resolvedRow.rallyIndex + 1) * 1000,
      }}
      defaultPlaybackRate={playbackRate}
    />
  ) : null;

  if (!open) return null;

  const happyPathHooksEnabled =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("happyPath") === "1";

  return (
    <Box
      data-happy-path="shot-target-lab-popup"
      data-shot-lab-view={viewMode}
      data-video-reference={showVideoReference ? "true" : "false"}
      data-shot-number={context?.shotNumber ?? resolvedShotNumber ?? ""}
      data-shot-time-sec={
        selectedOverlayVideoTimeSec > 0 ? selectedOverlayVideoTimeSec.toFixed(3) : ""
      }
      data-source-shot-number={opportunityContext?.shotNumber ?? ""}
      data-source-shot-time-sec={
        opportunitySourceShotTimeSec != null ? opportunitySourceShotTimeSec.toFixed(3) : ""
      }
      data-target-selected={labSelection.target ? "true" : "false"}
      data-path-selected={labSelection.pathLabel ? "true" : "false"}
      sx={{
        position: "fixed",
        left: frame.left,
        top: frame.top,
        width: frame.width,
        height: frame.height,
        zIndex: 10050,
        bgcolor: "rgba(12,12,12,0.98)",
        color: "rgba(255,255,255,0.92)",
        border: "1px solid rgba(255,255,255,0.22)",
        borderRadius: 1,
        boxShadow: "0 18px 70px rgba(0,0,0,0.50)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        isolation: "isolate",
      }}
    >
      <Box
        onMouseDown={(e) => {
          dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            startLeft: frame.left,
            startTop: frame.top,
          };
        }}
        sx={{
          px: 1.35,
          py: 0.85,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid rgba(255,255,255,0.12)",
          cursor: "move",
          userSelect: "none",
          position: "relative",
          zIndex: 70,
        }}
      >
        {opportunityContext && onBackToOpportunities ? (
          <IconButton
            size="small"
            aria-label="Back to opportunities"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={onBackToOpportunities}
            sx={{
              width: 32,
              height: 32,
              color: "rgba(255,255,255,0.86)",
              border: "1px solid rgba(255,255,255,0.18)",
              bgcolor: "rgba(255,255,255,0.06)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        ) : null}
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontSize: 18, fontWeight: 900 }}>
            {opportunityContext?.title ?? "Shot Target Lab"}
          </Box>
          {opportunityContext ? (
            <Box
              sx={{
                mt: 0.15,
                maxWidth: 760,
                color: "rgba(255,255,255,0.72)",
                fontSize: 12,
                fontWeight: 800,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <Box
                component="span"
                sx={{
                  px: 0.65,
                  py: 0.12,
                  borderRadius: 0.75,
                  color: "white",
                  bgcolor:
                    opportunityContext.outcome === "found"
                      ? "rgba(74, 170, 103, 0.45)"
                      : opportunityContext.outcome === "partial"
                        ? "rgba(225, 180, 72, 0.42)"
                        : "rgba(255,255,255,0.13)",
                }}
              >
                {opportunityContext.outcome}
              </Box>
              <Box
                component="span"
                sx={{
                  px: 0.65,
                  py: 0.12,
                  borderRadius: 0.75,
                  color: "white",
                  bgcolor:
                    opportunityContext.teamResult === "won" && opportunityContext.shotEndedRally
                      ? "rgba(74, 170, 103, 0.42)"
                      : opportunityContext.teamResult === "lost" && opportunityContext.shotEndedRally
                        ? "rgba(237, 94, 94, 0.36)"
                        : opportunityContext.teamResult === "won"
                          ? "rgba(74, 170, 103, 0.26)"
                          : opportunityContext.teamResult === "lost"
                            ? "rgba(237, 94, 94, 0.24)"
                            : "rgba(255,255,255,0.10)",
                }}
              >
                {opportunityResultEvidenceLabel(opportunityContext)}
              </Box>
              <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {opportunityContext.subtitle}
              </Box>
            </Box>
          ) : null}
        </Box>
        <Box sx={{ flex: 1 }} />
        <IconButton
          size="small"
          data-happy-path="shot-target-lab-close"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={onClose}
          sx={{ color: "rgba(255,255,255,0.72)" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          px: 1.35,
          py: 0.65,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid rgba(255,255,255,0.10)",
          flexWrap: "wrap",
          position: "relative",
          zIndex: 70,
        }}
      >
        <Box
          sx={{
            flex: "0 0 245px",
            fontWeight: 900,
            fontSize: 13,
            fontVariantNumeric: "tabular-nums",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          Rally {resolvedRow ? resolvedRow.rallyIndex + 1 : "?"} · Shot{" "}
          {context?.shotNumber ?? resolvedShotNumber}
          {resolvedRallyShotCount != null ? ` of ${resolvedRallyShotCount}` : ""}
          {selectedOverlayVideoTimeSec > 0
            ? ` · ${selectedOverlayVideoTimeSec.toFixed(2)}s`
            : ""}
        </Box>
        {opportunityContext ? (
          <Box
            sx={{
              flex: "1 1 340px",
              minWidth: 220,
              color: "rgba(255,255,255,0.66)",
              fontSize: 12,
              fontWeight: 750,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={opportunityContext.reason}
          >
            {opportunityContext.reason}
          </Box>
        ) : null}
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            const video = activeVideoRef.current?.current ?? null;
            if (video && Number.isFinite(video.currentTime)) {
              setPlaybackSeed({
                timeSec: Math.max(0, video.currentTime),
                paused: video.paused,
                nonce: Date.now(),
              });
            }
            ignoreCameraInteractionUntilRef.current = Date.now() + 500;
            restoreCurrentLabSelection();
            setViewMode("court3d");
            setShowVideoReference(true);
            setCourtFitActive(false);
            setCourtRotationSteps(0);
            setVideoPauseSnapArmed(false);
            setVideoViewResetNonce((n) => n + 1);
          }}
          sx={{ textTransform: "none", minHeight: 28, py: 0.1, ml: 1 }}
        >
          Reset default view
        </Button>
        {viewMode === "court3d" ? (
          <>
            <Button
              size="small"
              variant={showVideoReference ? "contained" : "outlined"}
              data-happy-path="shot-target-rally-video"
              onClick={() => {
                ignoreCameraInteractionUntilRef.current = Date.now() + 500;
                restoreCurrentLabSelection();
                setShowVideoReference(true);
                setCourtRotationSteps(0);
                setVideoViewResetNonce((n) => n + 1);
              }}
              sx={{ textTransform: "none", minHeight: 28, py: 0.1 }}
            >
              Rally video
            </Button>
            <Button
              size="small"
              variant={showCalibration ? "contained" : "outlined"}
              onClick={() => setShowCalibration((v) => !v)}
              sx={{ textTransform: "none", minHeight: 28, py: 0.1 }}
            >
              Calibrate 3D View
            </Button>
            {happyPathHooksEnabled ? (
              <>
                <button
                  type="button"
                  data-happy-path="shot-target-force-demo-shot"
                  tabIndex={-1}
                  aria-hidden="true"
                  style={{ display: "none" }}
                  onClick={() => {
                    const preferredOverlay =
                      contactOverlays.find((overlay) => overlay.shotNumber === 2) ??
                      contactOverlays.find((overlay) => overlay.shotNumber === 3) ??
                      contactOverlays.find((overlay) => overlay.shotNumber > 1) ??
                      null;
                    if (!preferredOverlay) return;
                    ignoreCameraInteractionUntilRef.current = Date.now() + 500;
                    setViewMode("court3d");
                    setShowVideoReference(true);
                    setCourtFitActive(false);
                    snapToShotContact(preferredOverlay, true);
                  }}
                >
                  Happy path demo shot
                </button>
                <button
                  type="button"
                  data-happy-path="shot-target-force-source-shot"
                  tabIndex={-1}
                  aria-hidden="true"
                  style={{ display: "none" }}
                  onClick={() => {
                    const sourceShotNumber =
                      opportunitySourceShotNumber ?? context?.shotNumber ?? resolvedShotNumber;
                    const sourceOverlay =
                      contactOverlays.find((overlay) => overlay.shotNumber === sourceShotNumber) ??
                      null;
                    if (!sourceOverlay) return;
                    ignoreCameraInteractionUntilRef.current = Date.now() + 500;
                    setViewMode("court3d");
                    setShowVideoReference(true);
                    setCourtFitActive(false);
                    setCourtRotationSteps(0);
                    setVideoPauseSnapArmed(false);
                    snapToShotContact(sourceOverlay, true);
                    setVideoViewResetNonce((n) => n + 1);
                  }}
                >
                  Happy path source shot
                </button>
                <button
                  type="button"
                  data-happy-path="shot-target-force-3d"
                  tabIndex={-1}
                  aria-hidden="true"
                  style={{ display: "none" }}
                  onClick={() => {
                    ignoreCameraInteractionUntilRef.current = Date.now() + 500;
                    setViewMode("court3d");
                    setShowVideoReference(false);
                    setCourtFitActive(false);
                  }}
                >
                  Happy path 3D view
                </button>
              </>
            ) : null}
          </>
        ) : null}
        <Button
          size="small"
          variant={currentBookmark ? "contained" : "outlined"}
          disabled={!resolvedRow}
          onClick={openBookmarkEditor}
          sx={{ textTransform: "none", minHeight: 28, py: 0.1 }}
        >
          {currentBookmark ? "Edit bookmark" : "Bookmark shot"}
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setBookmarksOpen(true)}
          sx={{ textTransform: "none", minHeight: 28, py: 0.1 }}
        >
          Bookmarks{bookmarks.length ? ` (${bookmarks.length})` : ""}
        </Button>
        {ENABLE_RALLY_REVIEW_AGENT ? (
          <Button
            size="small"
            variant={rallyReview ? "contained" : "outlined"}
            disabled={!resolvedRow || rallyReviewLoading}
            onClick={runRallyReviewAgent}
            sx={{ textTransform: "none", minHeight: 28, py: 0.1 }}
          >
            {rallyReviewLoading ? "Reviewing..." : "Rally review"}
          </Button>
        ) : null}
      </Box>

      {!opportunityContext ? (
        <Box
          sx={{
            px: 1.35,
            py: 0.55,
            overflowX: "hidden",
            borderBottom: "1px solid rgba(255,255,255,0.10)",
            position: "relative",
            zIndex: 70,
          }}
        >
          <CondensedRallyTimeline_ContextOnly
            activeRally={resolvedRow}
            rows={rows}
            barsAreaHeightPx={62}
            colW={10}
            showLegend={true}
            showRallyIndex={true}
            showWinnerAndServe={true}
            firefightRallySet={firefightRallySet}
            onPickRally={(rallyIndex) => {
              const next = rows.find((candidate) => candidate.rallyIndex === rallyIndex);
              if (next) selectRally(next);
            }}
          />
        </Box>
      ) : null}

      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          {viewMode === "court3d" ? (
            <Box
              ref={stageRef}
              data-happy-path="shot-target-lab-stage"
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                overflow: "visible",
                zIndex: 1,
              }}
            >
              {videoNode ? (
                <Box
                  data-happy-path="shot-target-video-reference-layer"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    pointerEvents: "none",
                    opacity: showVideoReference ? 0.72 : 0,
                  }}
                >
                  {videoNode}
                </Box>
              ) : null}
              <Box
                sx={{
                  position: "absolute",
                  inset:
                    showVideoReference && videoContentRect
                      ? undefined
                      : 0,
                  left:
                    showVideoReference && videoContentRect
                      ? videoContentRect.left - SHOT_SPACE_VIEW_BLEED_PX
                      : undefined,
                  top:
                    showVideoReference && videoContentRect
                      ? videoContentRect.top - SHOT_SPACE_VIEW_BLEED_PX
                      : undefined,
                  width:
                    showVideoReference && videoContentRect
                      ? videoContentRect.width + SHOT_SPACE_VIEW_BLEED_PX * 2
                      : undefined,
                  height:
                    showVideoReference && videoContentRect
                      ? videoContentRect.height + SHOT_SPACE_VIEW_BLEED_PX * 2
                      : undefined,
                  zIndex: 3,
                  pointerEvents: "auto",
                }}
              >
                <ShotUniverse3DView
                  context={context}
                  movementContext={movementContext}
                  lingeringActualContext={lingeringActualShot?.context ?? null}
                  lingeringActualOpacity={lingeringActualOpacity}
                  shotFamily={family}
                  showActual={showActual}
                  showGhosts={showGhosts}
                  showTargets={opportunityContext ? true : showTargets}
                  cvJson={cvJson}
                  shotAbsMs={shotAbsMs}
                  cameraAdjust={cameraAdjust}
                  videoViewResetNonce={videoViewResetNonce}
                  rotationSteps={courtRotationSteps}
                  transparentBackground={showVideoReference}
                  useVideoCameraView={true}
                  hideVideoAlignedCourtLines={
                    hideVideoAlignedCourtLines || useProjectedVideoCourtLines
                  }
                  viewBleedPx={
                    showVideoReference && videoContentRect
                      ? SHOT_SPACE_VIEW_BLEED_PX
                      : 0
                  }
                  interactive={!videoControlsInteractionActive}
                  interactionBlockedRef={videoControlsInteractionActiveRef}
                  visibility={SHOT_UNIVERSE_VISIBILITY}
                  overlayPortalContainer={shotSpaceOverlayPortalEl}
                  suggestedTarget={opportunityContext?.target ?? null}
                  suggestedTargetPathLabel={
                    opportunityContext
                      ? opportunityContext.outcome === "found"
                        ? "Target reference path (editable)"
                        : "Suggested alternate path (editable)"
                      : undefined
                  }
                  focusCustomTrajectoryOnly={!!opportunityContext}
                  onCameraInteraction={handleShotSpaceCameraInteraction}
                  onVideoCourtProjection={handleVideoCourtProjection}
                  onLabSelectionChange={setLabSelection}
                  restoreLabSelection={restoreLabSelection}
                />
              </Box>
              {showVideoReference &&
              videoContentRect &&
              (courtFitActive || courtHomography || courtFitClicks.length || courtFitResiduals.length) ? (
                <Box
                  onClick={handleCourtFitClick}
                  onPointerDown={(event) => {
                    if (courtFitActive) event.stopPropagation();
                  }}
                  onMouseDown={(event) => {
                    if (courtFitActive) event.stopPropagation();
                  }}
                  sx={{
                    position: "absolute",
                    left: videoContentRect.left,
                    top: videoContentRect.top,
                    width: videoContentRect.width,
                    height: videoContentRect.height,
                    zIndex: 72,
                    pointerEvents: courtFitActive ? "auto" : "none",
                    cursor: nextCourtFitLandmark ? "crosshair" : "default",
                  }}
                >
                  {courtFitActive ? (
                    <Box
                      sx={{
                        position: "absolute",
                        left: 10,
                        top: 10,
                        px: 1,
                        py: 0.7,
                        maxWidth: 360,
                        border: "1px solid rgba(155,207,255,0.55)",
                        borderRadius: 1,
                        bgcolor: "rgba(0,0,0,0.72)",
                        color: "rgba(255,255,255,0.9)",
                        fontSize: 12,
                        lineHeight: 1.25,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
                      }}
                    >
                      <Box sx={{ fontWeight: 900, color: "#9bcfff" }}>
                        {nextCourtFitLandmark
                          ? `Click: ${nextCourtFitLandmark.label}`
                          : "Ready to solve"}
                      </Box>
                      <Box sx={{ color: "rgba(255,255,255,0.72)" }}>
                        {nextCourtFitLandmark?.hint ??
                          "Use Solve in the calibration panel, or undo a point."}
                      </Box>
                    </Box>
                  ) : null}
                  {courtHomography || videoCourtProjection ? (
                    <svg
                      width={videoContentRect.width}
                      height={videoContentRect.height}
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        overflow: "visible",
                        pointerEvents: "none",
                      }}
                    >
                      {courtHomography ? (
                        <>
                          <polyline
                            points={courtHomographyPolyline(courtHomography, COURT_DEBUG_POLYLINES[0])}
                            fill="none"
                            stroke="#00f5ff"
                            strokeWidth="3"
                            opacity="0.95"
                          />
                          {COURT_DEBUG_POLYLINES.slice(1).map((line, index) => (
                            <polyline
                              key={`court-homography-line-${index}`}
                              points={courtHomographyPolyline(
                                courtHomography,
                                line as Array<[number, number]>,
                              )}
                              fill="none"
                              stroke="#00f5ff"
                              strokeWidth={index === 0 ? "3" : "2.3"}
                              opacity={index === 0 ? "0.95" : "0.88"}
                            />
                          ))}
                        </>
                      ) : null}
                      {videoCourtProjection ? (
                        <>
                          <polyline
                            points={videoCourtProjection.outer}
                            fill="none"
                            stroke="#ff00d4"
                            strokeWidth="2.2"
                            strokeDasharray="8 5"
                            opacity="0.98"
                          />
                          {videoCourtProjection.lines.map((points, index) => (
                            <polyline
                              key={`live-r3f-camera-line-${index}`}
                              points={points}
                              fill="none"
                              stroke="#ff00d4"
                              strokeWidth={index === 0 ? "2.2" : "1.8"}
                              strokeDasharray="8 5"
                              opacity={index === 0 ? "0.98" : "0.9"}
                            />
                          ))}
                        </>
                      ) : null}
                    </svg>
                  ) : null}
                  {courtFitResiduals.map((residual) => {
                    const dx = residual.fitX - residual.clickX;
                    const dy = residual.fitY - residual.clickY;
                    const rawLength = Math.hypot(dx, dy);
                    const length = Math.min(rawLength, 140);
                    const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
                    const color =
                      residual.errorPx > 18
                        ? "#ff5a5a"
                        : residual.errorPx > 8
                          ? "#ffcf4a"
                          : "#70e38f";
                    return (
                      <React.Fragment key={`court-fit-residual-${residual.pointId}`}>
                        <Box
                          sx={{
                            position: "absolute",
                            left: residual.clickX,
                            top: residual.clickY,
                            width: length,
                            height: 0,
                            borderTop: `2px solid ${color}`,
                            transform: `rotate(${angle}deg)`,
                            transformOrigin: "0 0",
                            opacity: 0.92,
                            pointerEvents: "none",
                            zIndex: 1,
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            left: residual.fitX,
                            top: residual.fitY,
                            width: 10,
                            height: 10,
                            transform: "translate(-50%, -50%)",
                            borderRadius: "50%",
                            border: `2px solid ${color}`,
                            bgcolor: "rgba(0,0,0,0.45)",
                            pointerEvents: "none",
                            zIndex: 2,
                          }}
                        />
                      </React.Fragment>
                    );
                  })}
                  {courtFitActive
                    ? courtFitClicks.map((click, index) => {
                        const landmark = COURT_FIT_LANDMARKS.find(
                          (candidate) => candidate.id === click.pointId,
                        );
                        return (
                          <Box
                            key={click.pointId}
                          sx={{
                            position: "absolute",
                            left: click.x,
                            top: click.y,
                            width: 18,
                            height: 18,
                            transform: "translate(-50%, -50%)",
                            borderRadius: "50%",
                            border: "2px solid #ffffff",
                            bgcolor: "#2a82ff",
                            boxShadow: "0 0 0 3px rgba(42,130,255,0.35)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#ffffff",
                            fontSize: 10,
                            fontWeight: 900,
                            pointerEvents: "none",
                          }}
                        >
                            {index + 1}
                            <Box
                              sx={{
                                position: "absolute",
                                left: 22,
                                top: -4,
                                px: 0.55,
                                py: 0.2,
                                minWidth: 110,
                                borderRadius: 0.7,
                                bgcolor: "rgba(0,0,0,0.68)",
                                color: "rgba(255,255,255,0.86)",
                                fontSize: 10.5,
                                fontWeight: 800,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {landmark?.label ?? click.pointId}
                            </Box>
                          </Box>
                        );
                      })
                    : null}
                </Box>
              ) : null}
              <Box
                ref={setVideoControlsPortalEl}
                onPointerDown={(e) => e.stopPropagation()}
                onPointerMove={(e) => e.stopPropagation()}
                onPointerUp={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseMove={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 35,
                  overflow: "visible",
                  pointerEvents: "none",
                }}
              />
              <Box
                ref={setVideoLoopControlsPortalEl}
                onPointerDown={(e) => {
                  handleVideoControlsInteractionChange(true);
                  e.stopPropagation();
                }}
                onPointerMove={(e) => e.stopPropagation()}
                onPointerUp={(e) => {
                  handleVideoControlsInteractionChange(false);
                  e.stopPropagation();
                }}
                onPointerCancel={(e) => {
                  handleVideoControlsInteractionChange(false);
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  handleVideoControlsInteractionChange(true);
                  e.stopPropagation();
                }}
                onMouseMove={(e) => e.stopPropagation()}
                onMouseUp={(e) => {
                  handleVideoControlsInteractionChange(false);
                  e.stopPropagation();
                }}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: 180,
                  zIndex: 200,
                  overflow: "visible",
                  pointerEvents: "auto",
                  touchAction: "none",
                  userSelect: "none",
                }}
              />
              <Box
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  position: "absolute",
                  right: 12,
                  top: 10,
                  zIndex: 38,
                  pointerEvents: "auto",
                }}
              >
                {SHOW_SHOT_SWITCH_TIMING_TUNER && showTimingTuner ? (
                  <Box
                    sx={{
                      width: 260,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: "rgba(0,0,0,0.74)",
                      border: "1px solid rgba(255,255,255,0.18)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
                      color: "rgba(255,255,255,0.88)",
                      fontSize: 12,
                      "& .MuiSlider-root": { py: 0.4 },
                      "& .MuiButton-root": {
                        minHeight: 24,
                        py: 0,
                        textTransform: "none",
                        fontSize: 11.5,
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.5 }}>
                      <Box sx={{ fontWeight: 900 }}>Shot switch timing</Box>
                      <Box sx={{ flex: 1 }} />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setShowTimingTuner(false)}
                      >
                        Hide
                      </Button>
                    </Box>
                    <Box sx={{ color: "rgba(255,255,255,0.62)", mb: 0.65 }}>
                      Controls when targets/tubes switch to the next shot.
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.6, mb: 0.8 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          setShotSwitchTiming(cloneShotSwitchTiming(LEGACY_SHOT_SWITCH_TIMING))
                        }
                      >
                        Current
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          setShotSwitchTiming(cloneShotSwitchTiming(DEFAULT_SHOT_SWITCH_TIMING))
                        }
                      >
                        Earlier
                      </Button>
                    </Box>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "74px 1fr 44px",
                        gap: 0.8,
                        alignItems: "center",
                      }}
                    >
                      <Box>fraction</Box>
                      <Slider
                        size="small"
                        min={0.15}
                        max={0.75}
                        step={0.01}
                        value={normalizedShotSwitchTiming.fraction}
                        onChange={(_, value) =>
                          setShotSwitchTiming((cur) => ({
                            ...cur,
                            fraction: Number(value),
                          }))
                        }
                      />
                      <Box sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {normalizedShotSwitchTiming.fraction.toFixed(2)}
                      </Box>
                      <Box
                        sx={{
                          gridColumn: "1 / -1",
                          mt: -0.55,
                          mb: 0.35,
                          color: "rgba(255,255,255,0.56)",
                        }}
                      >
                        Lower switches sooner between contacts; higher waits longer.
                      </Box>

                      <Box>hold shot</Box>
                      <Slider
                        size="small"
                        min={0}
                        max={0.35}
                        step={0.01}
                        value={normalizedShotSwitchTiming.holdCurrentFraction}
                        onChange={(_, value) =>
                          setShotSwitchTiming((cur) => ({
                            ...cur,
                            holdCurrentFraction: Number(value),
                          }))
                        }
                      />
                      <Box sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {normalizedShotSwitchTiming.holdCurrentFraction.toFixed(2)}
                      </Box>
                      <Box
                        sx={{
                          gridColumn: "1 / -1",
                          mt: -0.55,
                          mb: 0.35,
                          color: "rgba(255,255,255,0.56)",
                        }}
                      >
                        Minimum slice of the shot interval to keep the current shot visible.
                      </Box>

                      <Box>preview</Box>
                      <Slider
                        size="small"
                        min={0}
                        max={0.35}
                        step={0.01}
                        value={normalizedShotSwitchTiming.previewNextFraction}
                        onChange={(_, value) =>
                          setShotSwitchTiming((cur) => ({
                            ...cur,
                            previewNextFraction: Number(value),
                          }))
                        }
                      />
                      <Box sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {normalizedShotSwitchTiming.previewNextFraction.toFixed(2)}
                      </Box>
                      <Box
                        sx={{
                          gridColumn: "1 / -1",
                          mt: -0.55,
                          color: "rgba(255,255,255,0.56)",
                        }}
                      >
                        Minimum slice before next contact to show the next shot context.
                      </Box>
                    </Box>
                  </Box>
                ) : SHOW_SHOT_SWITCH_TIMING_TUNER ? (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => setShowTimingTuner(true)}
                    sx={{
                      textTransform: "none",
                      minHeight: 26,
                      py: 0.15,
                      boxShadow: "0 6px 20px rgba(0,0,0,0.5)",
                    }}
                  >
                    Timing
                  </Button>
                ) : null}
              </Box>
            </Box>
          ) : resolvedRow ? (
            videoNode
          ) : (
            <Box sx={{ p: 2, color: "rgba(255,255,255,0.72)" }}>
              No rally is selected.
            </Box>
          )}
        </Box>

        {(showCalibration ||
          SHOW_TIMING_DEBUG ||
          (ENABLE_RALLY_REVIEW_AGENT && (rallyReview || rallyReviewError))) ? (
          <Box
            sx={{
              position: "relative",
              zIndex: 60,
              width: 300,
              borderLeft: "1px solid rgba(255,255,255,0.12)",
              p: 1.2,
              fontSize: 12.5,
              color: "rgba(255,255,255,0.76)",
              overflowY: "auto",
              pointerEvents: "auto",
            }}
          >
          {ENABLE_RALLY_REVIEW_AGENT && (rallyReview || rallyReviewError) ? (
            <Box
              sx={{
                mb: 1.1,
                pb: 1,
                borderBottom: "1px solid rgba(255,255,255,0.14)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.6 }}>
                <Box sx={{ fontWeight: 900, color: "rgba(255,255,255,0.92)" }}>
                  Rally review
                </Box>
                <Box sx={{ flex: 1 }} />
                {rallyReview ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setRallyReview(null);
                      setRallyReviewError(null);
                      setRallyReviewLoopSeed(null);
                    }}
                    sx={{ textTransform: "none", minHeight: 24, py: 0 }}
                  >
                    Clear
                  </Button>
                ) : null}
              </Box>
              {rallyReviewError ? (
                <Box sx={{ color: "error.main" }}>{rallyReviewError}</Box>
              ) : null}
              {rallyReview ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.55 }}>
                  <Box sx={{ fontWeight: 850, color: "rgba(255,255,255,0.9)" }}>
                    {rallyReview.headline}
                  </Box>
                  <Box>
                    Inspect Shot {rallyReview.primaryMoment.shotNumber}:{" "}
                    {rallyReview.primaryMoment.reason}
                  </Box>
                  <Box>
                    Loop {(rallyReview.suggestedLoop.startMs / 1000).toFixed(2)}s-
                    {(rallyReview.suggestedLoop.endMs / 1000).toFixed(2)}s ·{" "}
                    {rallyReview.suggestedLoop.speed}x
                  </Box>
                  <Box>Confidence: {rallyReview.confidence}</Box>
                  {rallyReview.reviewPipeline ? (
                    <Box>
                      Pipeline: candidate pre-pass → {rallyReview.reviewPipeline.modelCallCount ?? 1}{" "}
                      model call · {rallyReview.reviewPipeline.candidateCount} moments
                      {rallyReview.reviewPipeline.contextBytes != null
                        ? ` · ${(rallyReview.reviewPipeline.contextBytes / 1024).toFixed(1)}KB context`
                        : ""}
                    </Box>
                  ) : null}
                  {rallyReview.nextQuestion ? (
                    <Box>Next: {rallyReview.nextQuestion}</Box>
                  ) : null}
                  {rallyReview.dataLimitations.length ? (
                    <Box sx={{ color: "rgba(255,255,255,0.62)" }}>
                      Limits: {rallyReview.dataLimitations.join(" ")}
                    </Box>
                  ) : null}
                  {rallyReview.aiUsage ? (
                    <Box
                      sx={{
                        mt: 0.35,
                        pt: 0.55,
                        borderTop: "1px solid rgba(255,255,255,0.12)",
                        color: "rgba(255,255,255,0.66)",
                      }}
                    >
                      AI usage:{" "}
                      {rallyReview.aiUsage.totalTokens == null
                        ? "tokens unavailable"
                        : `${rallyReview.aiUsage.totalTokens} tokens`}{" "}
                      ({rallyReview.aiUsage.inputTokens ?? "?"} in /{" "}
                      {rallyReview.aiUsage.outputTokens ?? "?"} out)
                      <Box>Log route: {rallyReview.aiUsage.logRoute}</Box>
                    </Box>
                  ) : null}
                  {rallyReview.reviewPipeline?.candidates?.length ? (
                    <Box sx={{ color: "rgba(255,255,255,0.58)" }}>
                      <Box sx={{ fontWeight: 800, color: "rgba(255,255,255,0.68)" }}>
                        Candidate shortlist
                      </Box>
                      {rallyReview.reviewPipeline.candidates.slice(0, 5).map((candidate) => (
                        <Box key={`rally-review-candidate-${candidate.shotNumber}`}>
                          S{candidate.shotNumber}
                          {candidate.score != null ? ` · score ${candidate.score}: ` : ": "}
                          {candidate.reasons.slice(0, 2).join(", ")}
                        </Box>
                      ))}
                    </Box>
                  ) : null}
                </Box>
              ) : null}
            </Box>
          ) : null}
          {(showCalibration || SHOW_TIMING_DEBUG) ? (
            <>
          <Box sx={{ fontWeight: 900, color: "rgba(255,255,255,0.92)", mb: 0.7 }}>
            First-pass read
          </Box>
          <Box>Shot type: {context?.selectedShotType ?? "unknown"}</Box>
          <Box>Hitter: {context?.hitterPlayerId == null ? "unknown" : `p${context.hitterPlayerId}`}</Box>
          <Box>Ghost options: {context?.options.length ?? 0}</Box>
          {SHOW_TIMING_DEBUG && debugTiming ? (
            <Box
              sx={{
                mt: 1.1,
                pt: 0.9,
                borderTop: "1px solid rgba(255,255,255,0.14)",
                color: "rgba(255,255,255,0.74)",
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
                fontSize: 11.5,
                lineHeight: 1.35,
              }}
            >
              <Box sx={{ fontFamily: "inherit", fontWeight: 900, color: "rgba(255,255,255,0.9)", mb: 0.45 }}>
                Timing debug
              </Box>
              <Box>video current: {formatDebugSec(debugTiming.videoSec)}</Box>
              <Box>
                selected/mid/label/near: S{debugTiming.selectedShot ?? "?"} / S
                {debugTiming.midpointShot ?? "?"} / S
                {debugTiming.playbackShot ?? "?"} / S{debugTiming.nearestShot ?? "?"}
              </Box>
              <Box>
                movement contact/ctx/next: S{debugTiming.contactedShot ?? "?"} / S
                {debugTiming.movementShot ?? "?"} / S{debugTiming.movementNextShot ?? "-"} · cues{" "}
                {debugTiming.movementCueCount} · players {debugTiming.movementPlayerCount}
              </Box>
              <Box>
                prev/mid/next: S{debugTiming.previousShot ?? "-"} / S
                {debugTiming.midpointShot ?? "?"} / S{debugTiming.nextShot ?? "-"}
              </Box>
              <Box>selected norm: {formatDebugMs(debugTiming.normalizedSelectedMs)}</Box>
              <Box>
                midpoint norm: {formatDebugMs(debugTiming.normalizedMidpointMs)} (f={normalizedShotSwitchTiming.fraction.toFixed(2)})
              </Box>
              <Box>prev contact: {formatDebugMs(debugTiming.previousContactMs)}</Box>
              <Box>mid contact: {formatDebugMs(debugTiming.midpointContactMs)}</Box>
              <Box>next contact: {formatDebugMs(debugTiming.nextContactMs)}</Box>
              <Box>mid switch: {formatDebugMs(debugTiming.midpointSwitchMs)}</Box>
              <Box>next switch: {formatDebugMs(debugTiming.nextSwitchMs)}</Box>
              <Box>nearest norm: {formatDebugMs(debugTiming.normalizedNearestMs)}</Box>
              <Box>
                midpoint update: S{debugTiming.lastMidpointUpdate?.shotNumber ?? "?"} @{" "}
                {formatDebugSec(debugTiming.lastMidpointUpdate?.videoSec ?? null)}
              </Box>
              <Box>
                label event: S{debugTiming.lastLabelUpdate?.shotNumber ?? "?"} @{" "}
                {formatDebugSec(debugTiming.lastLabelUpdate?.videoSec ?? null)}
              </Box>
              <Box sx={{ mt: 0.45 }}>shot window:</Box>
              {debugTiming.shotWindow.map((shot) => (
                <Box key={`debug-shot-${shot.shotNumber}`}>
                  S{shot.shotNumber}: sw {formatDebugMs(shot.switchMs)} · ct{" "}
                  {formatDebugMs(shot.contactMs)}
                </Box>
              ))}
              <Box>seed: {formatDebugSec(debugTiming.seedSec)} {debugTiming.seedPaused == null ? "" : debugTiming.seedPaused ? "paused" : "playing"}</Box>
              <Box>overlay base: {formatDebugMs(debugTiming.baseMs)}</Box>
              <Box>seg start: {formatDebugMs(debugTiming.segmentStartMs)}</Box>
              <Box>seg end: {formatDebugMs(debugTiming.segmentEndMs)}</Box>
              <Box>raw atMsRel: {formatDebugMs(debugTiming.rawAtMsRel)}</Box>
              <Box>raw traj start: {formatDebugMs(debugTiming.rawTrajectoryStartMs)}</Box>
              <Box>raw shot start: {formatDebugMs(debugTiming.rawShotStartMs)}</Box>
              <Box>
                suppress: {Math.round(debugTiming.suppressMsLeft)}ms below S
                {debugTiming.suppressBelow ?? "-"}
              </Box>
            </Box>
          ) : null}
          <Box sx={{ mt: 1.2, fontWeight: 900, color: "rgba(255,255,255,0.9)" }}>
            Heuristic labels
          </Box>
          <Box sx={{ mt: 0.35 }}>Green: safer neutral target</Box>
          <Box>Gold: more aggressive/open target</Box>
          <Box>Red: provisional bad/risky target</Box>
          <Box sx={{ mt: 1.2 }}>
            This is deliberately approximate: target scoring only considers open
            space, opponent distance, basic in/out risk, and crude shot family.
          </Box>
          {viewMode === "court3d" && showCalibration ? (
            <Box
              sx={{
                mt: 1.4,
                pt: 1.1,
                borderTop: "1px solid rgba(255,255,255,0.14)",
              }}
            >
              <Box sx={{ fontWeight: 900, color: "rgba(255,255,255,0.9)", mb: 0.75 }}>
                View calibration
              </Box>
              <Box sx={{ mb: 0.8 }}>
                Saved for this game in localStorage. Use these while the video is visible;
                orbiting the court hides the video reference.
              </Box>
              <Box
                sx={{
                  mb: 1.15,
                  p: 0.85,
                  border: "1px solid rgba(255,255,255,0.14)",
                  borderRadius: 1,
                  bgcolor: courtFitActive
                    ? "rgba(42,130,255,0.14)"
                    : "rgba(255,255,255,0.045)",
                }}
              >
                <Box sx={{ fontWeight: 850, color: "rgba(255,255,255,0.88)", mb: 0.55 }}>
                  Court-line fit
                </Box>
                <Box sx={{ color: "rgba(255,255,255,0.68)", mb: 0.7 }}>
                  Load PBV cv.json court points first; manually click points only if the CV fit needs help.
                </Box>
                <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap", mb: 0.8 }}>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!videoContentRect}
                    onClick={loadCvCourtFitClicks}
                    sx={{ textTransform: "none", minHeight: 24, py: 0 }}
                  >
                    Load CV points
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!courtHomography || !videoContentRect}
                    onClick={applyPlanarToCamera}
                    sx={{ textTransform: "none", minHeight: 24, py: 0 }}
                  >
                    Apply to 3D camera
                  </Button>
                  <Button
                    size="small"
                    variant={hideVideoAlignedCourtLines ? "contained" : "outlined"}
                    onClick={() => setHideVideoAlignedCourtLines((hidden) => !hidden)}
                    sx={{ textTransform: "none", minHeight: 24, py: 0 }}
                  >
                    {hideVideoAlignedCourtLines ? "Show 3D lines" : "Hide 3D lines"}
                  </Button>
                  <Button
                    size="small"
                    variant={courtFitActive ? "contained" : "outlined"}
                    onClick={() => {
                      setCourtFitActive((active) => !active);
                      setShowVideoReference(true);
                      setCourtFitErrorPx(null);
                    }}
                    sx={{ textTransform: "none", minHeight: 24, py: 0 }}
                  >
                    {courtFitActive ? "Manual clicking on" : "Manual click"}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!courtFitClicks.length}
                    onClick={undoCourtFitClick}
                    sx={{ textTransform: "none", minHeight: 24, py: 0 }}
                  >
                    Undo
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!courtFitClicks.length}
                    onClick={clearCourtFitClicks}
                    sx={{ textTransform: "none", minHeight: 24, py: 0 }}
                  >
                    Clear
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={courtFitClicks.length < 4 || !videoContentRect}
                    onClick={applyCourtFit}
                    sx={{ textTransform: "none", minHeight: 24, py: 0 }}
                  >
                    Solve
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={planarEquationCount(courtFitClicks) < 8}
                    onClick={applyCourtHomography}
                    sx={{ textTransform: "none", minHeight: 24, py: 0 }}
                  >
                    Planar preview
                  </Button>
                </Box>
                {courtFitActive ? (
                  <Box sx={{ color: "rgba(255,255,255,0.76)" }}>
                    {nextCourtFitLandmark ? (
                    <>
                      Next:{" "}
                      <Box component="span" sx={{ fontWeight: 850, color: "#9bcfff" }}>
                        {nextCourtFitLandmark.label}
                      </Box>
                      <Box sx={{ color: "rgba(255,255,255,0.58)" }}>
                        {nextCourtFitLandmark.hint}
                      </Box>
                    </>
                    ) : (
                      "All fit points clicked."
                    )}
                  </Box>
                ) : null}
                <Box sx={{ mt: 0.55, color: "rgba(255,255,255,0.62)" }}>
                  {courtFitClicks.length}/{COURT_FIT_LANDMARKS.length} points
                  {courtFitErrorPx != null
                    ? ` · average error ${courtFitErrorPx.toFixed(1)}px`
                    : ""}
                </Box>
                {courtFitClicks.length ? (
                  <Box
                    sx={{
                      mt: 0.65,
                      maxHeight: 138,
                      overflowY: "auto",
                      borderTop: "1px solid rgba(255,255,255,0.11)",
                      pt: 0.55,
                    }}
                  >
                    <Box sx={{ fontWeight: 800, color: "rgba(255,255,255,0.72)", mb: 0.35 }}>
                      Saved points
                    </Box>
                    {courtFitClicks.map((click, index) => {
                      const landmark = COURT_FIT_LANDMARKS.find(
                        (candidate) => candidate.id === click.pointId,
                      );
                      return (
                        <Box
                          key={`court-fit-saved-${click.pointId}`}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "18px 1fr auto auto",
                            gap: 0.55,
                            alignItems: "center",
                            mb: 0.35,
                          }}
                        >
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: "50%",
                              bgcolor: "#2a82ff",
                              color: "#fff",
                              fontSize: 10,
                              fontWeight: 900,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Box
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color: "rgba(255,255,255,0.78)",
                            }}
                            title={landmark?.label ?? click.pointId}
                          >
                            {landmark?.label ?? click.pointId}
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => removeCourtFitClick(click.pointId, true)}
                            sx={{ textTransform: "none", minHeight: 20, py: 0, px: 0.55 }}
                          >
                            Redo
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => removeCourtFitClick(click.pointId, false)}
                            sx={{ textTransform: "none", minHeight: 20, py: 0, px: 0.55 }}
                          >
                            Delete
                          </Button>
                        </Box>
                      );
                    })}
                  </Box>
                ) : null}
                {courtFitSolveStatus ? (
                  <Box sx={{ mt: 0.35, color: "rgba(155,207,255,0.9)" }}>
                    {courtFitSolveStatus}
                  </Box>
                ) : null}
                {courtHomographyStatus ? (
                  <Box sx={{ mt: 0.35, color: "rgba(142,230,142,0.9)" }}>
                    {courtHomographyStatus}
                  </Box>
                ) : null}
                {liveProjectionStats != null ? (
                  <Box sx={{ mt: 0.35, color: "rgba(255,212,0,0.92)" }}>
                    Live 3D camera vs green: avg {liveProjectionStats.averagePx.toFixed(1)}px ·
                    p90 {liveProjectionStats.p90Px.toFixed(1)}px · max{" "}
                    {liveProjectionStats.maxPx.toFixed(1)}px
                    {liveProjectionStats.worstLine ? ` (${liveProjectionStats.worstLine})` : ""}
                  </Box>
                ) : null}
                {courtFitResiduals.length ? (
                  <Box sx={{ mt: 0.45, color: "rgba(255,255,255,0.62)" }}>
                    <Box sx={{ fontWeight: 800, color: "rgba(255,255,255,0.72)" }}>
                      Worst residuals
                    </Box>
                    {courtFitResiduals
                      .slice()
                      .sort((a, b) => b.errorPx - a.errorPx)
                      .slice(0, 4)
                      .map((residual) => (
                        <Box
                          key={`court-fit-residual-summary-${residual.pointId}`}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr auto",
                            gap: 0.7,
                            color:
                              residual.errorPx > 18
                                ? "#ff8a8a"
                                : residual.errorPx > 8
                                  ? "#ffdf76"
                                  : "rgba(255,255,255,0.62)",
                          }}
                        >
                          <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {residual.label}
                          </Box>
                          <Box>{residual.errorPx.toFixed(1)}px</Box>
                        </Box>
                      ))}
                  </Box>
                ) : null}
              </Box>
              {(
                [
                  ["position", "Camera", -35, 35, ["X", "Y", "Z"]],
                  ["target", "Look at", -35, 35, ["X", "Y", "Z"]],
                ] as const
              ).map(([kind, label, min, max, axes]) => (
                <Box key={kind} sx={{ mb: 1.05 }}>
                  <Box sx={{ fontWeight: 800, color: "rgba(255,255,255,0.86)" }}>{label}</Box>
                  {axes.map((axis, i) => (
                    <Box
                      key={`${kind}-${axis}`}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "34px 30px 30px 1fr 44px",
                        gap: 0.7,
                        alignItems: "center",
                      }}
                    >
                      <Box>{axis}</Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          nudgeCameraAdjust(kind, i as 0 | 1 | 2, -0.5, min, max)
                        }
                        sx={{ minWidth: 26, height: 22, px: 0, lineHeight: 1 }}
                      >
                        -0.5
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          nudgeCameraAdjust(kind, i as 0 | 1 | 2, 0.5, min, max)
                        }
                        sx={{ minWidth: 26, height: 22, px: 0, lineHeight: 1 }}
                      >
                        +0.5
                      </Button>
                      <Slider
                        size="small"
                        min={min}
                        max={max}
                        step={0.5}
                        value={cameraAdjust[kind][i]}
                        onChange={(_, value) =>
                          updateCameraAdjust(kind, i as 0 | 1 | 2, Number(value))
                        }
                      />
                      <Box sx={{ textAlign: "right" }}>
                        {formatNumber(cameraAdjust[kind][i])}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ))}
              <Box sx={{ mb: 1.05 }}>
                <Box sx={{ fontWeight: 800, color: "rgba(255,255,255,0.86)" }}>FOV</Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "34px 30px 30px 1fr 44px",
                    gap: 0.7,
                    alignItems: "center",
                  }}
                >
                  <Box>Δ</Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => updateFovAdjust(cameraAdjust.fov - 1)}
                    sx={{ minWidth: 26, height: 22, px: 0, lineHeight: 1 }}
                  >
                    -1
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => updateFovAdjust(cameraAdjust.fov + 1)}
                    sx={{ minWidth: 26, height: 22, px: 0, lineHeight: 1 }}
                  >
                    +1
                  </Button>
                  <Slider
                    size="small"
                    min={-35}
                    max={35}
                    step={1}
                    value={cameraAdjust.fov}
                    onChange={(_, value) => updateFovAdjust(Number(value))}
                  />
                  <Box sx={{ textAlign: "right" }}>{formatNumber(cameraAdjust.fov)}</Box>
                </Box>
              </Box>
              <Box sx={{ mb: 1.05 }}>
                <Box sx={{ fontWeight: 800, color: "rgba(255,255,255,0.86)" }}>
                  Roll
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "34px 30px 30px 1fr 44px",
                    gap: 0.7,
                    alignItems: "center",
                  }}
                >
                  <Box>deg</Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => updateRollAdjust((cameraAdjust.roll ?? 0) - 0.5)}
                    sx={{ minWidth: 26, height: 22, px: 0, lineHeight: 1 }}
                  >
                    -0.5
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => updateRollAdjust((cameraAdjust.roll ?? 0) + 0.5)}
                    sx={{ minWidth: 26, height: 22, px: 0, lineHeight: 1 }}
                  >
                    +0.5
                  </Button>
                  <Slider
                    size="small"
                    min={-20}
                    max={20}
                    step={0.1}
                    value={cameraAdjust.roll ?? 0}
                    onChange={(_, value) => updateRollAdjust(Number(value))}
                  />
                  <Box sx={{ textAlign: "right" }}>{formatNumber(cameraAdjust.roll ?? 0)}</Box>
                </Box>
              </Box>
              <Box sx={{ mb: 1.05 }}>
                <Box sx={{ fontWeight: 800, color: "rgba(255,255,255,0.86)" }}>
                  Projection offset
                </Box>
                {(["X", "Y"] as const).map((axis, i) => (
                  <Box
                    key={`offset-${axis}`}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "34px 30px 30px 1fr 44px",
                      gap: 0.7,
                      alignItems: "center",
                    }}
                  >
                    <Box>{axis}</Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => nudgeOffsetAdjust(i as 0 | 1, -4)}
                      sx={{ minWidth: 26, height: 22, px: 0, lineHeight: 1 }}
                    >
                      -4
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => nudgeOffsetAdjust(i as 0 | 1, 4)}
                      sx={{ minWidth: 26, height: 22, px: 0, lineHeight: 1 }}
                    >
                      +4
                    </Button>
                    <Slider
                      size="small"
                      min={-240}
                      max={240}
                      step={1}
                      value={cameraAdjust.offset?.[i] ?? 0}
                      onChange={(_, value) => {
                        const nextValue = Number(value);
                        setCameraAdjust((cur) => {
                          const next = cloneCameraAdjust(cur);
                          const offset = next.offset ?? [0, 0];
                          offset[i] = clampCalibrationValue(nextValue, -240, 240);
                          next.offset = [offset[0], offset[1]];
                          saveCameraAdjust(packageId, next);
                          return next;
                        });
                        setVideoViewResetNonce((n) => n + 1);
                      }}
                    />
                    <Box sx={{ textAlign: "right" }}>
                      {formatNumber(cameraAdjust.offset?.[i] ?? 0)}
                    </Box>
                  </Box>
                ))}
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={resetCameraAdjust}
                sx={{ textTransform: "none", minHeight: 28, py: 0.1, mr: 0.75 }}
              >
                Reset saved calibration
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={clearAllCalibration}
                sx={{ textTransform: "none", minHeight: 28, py: 0.1 }}
              >
                Clear local calibration
              </Button>
            </Box>
          ) : null}
            </>
          ) : null}
        </Box>
      ) : null}
      </Box>

      {bookmarkEditorOpen ? (
        <Box
          onPointerDown={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          sx={{
            position: "absolute",
            right: 18,
            top: 96,
            width: 340,
            zIndex: 80,
            p: 1.2,
            border: "1px solid rgba(255,255,255,0.25)",
            borderRadius: 1,
            bgcolor: "rgba(8,8,8,0.98)",
            color: "#fff",
            boxShadow: "0 14px 44px rgba(0,0,0,0.64)",
          }}
        >
          <Box sx={{ fontWeight: 900, mb: 0.75 }}>
            {currentBookmark ? "Edit bookmark" : "Bookmark this shot"}
          </Box>
          <Box
            sx={{
              mb: 0.75,
              color: "rgba(255,255,255,0.68)",
              fontSize: 12.5,
              fontWeight: 800,
            }}
          >
            {bookmarkContextLabel}
          </Box>
          <Box
            component="textarea"
            value={bookmarkDescriptionDraft}
            placeholder="Reason for bookmarking"
            onChange={(event) => setBookmarkDescriptionDraft(event.target.value)}
            sx={{
              width: "100%",
              minHeight: 86,
              resize: "vertical",
              boxSizing: "border-box",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 0.75,
              bgcolor: "rgba(0,0,0,0.5)",
              color: "#fff",
              px: 1,
              py: 0.8,
              font: "inherit",
              fontSize: 13,
              outline: "none",
            }}
          />
          <Box sx={{ mt: 0.7, color: "rgba(255,255,255,0.6)", fontSize: 12 }}>
            {viewMode === "court3d" &&
            (labSelection.target || labSelection.targets?.length)
              ? `${labSelection.targets?.length || 1} target${
                  (labSelection.targets?.length || 1) === 1 ? "" : "s"
                } will be saved for the lab.`
              : "No lab target is selected yet."}
          </Box>
          <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end", gap: 0.75 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setBookmarkEditorOpen(false)}
              sx={{ textTransform: "none", minHeight: 28 }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={saveCurrentBookmark}
              sx={{ textTransform: "none", minHeight: 28 }}
            >
              Save
            </Button>
          </Box>
        </Box>
      ) : null}

      <ShotBookmarksPopup
        open={bookmarksOpen}
        bookmarks={bookmarks}
        onClose={() => setBookmarksOpen(false)}
        onWatch={(bookmark) => {
          if (onWatchBookmark) {
            setBookmarksOpen(false);
            onWatchBookmark(bookmark);
            return;
          }
          openBookmarkInLab(bookmark, "video");
        }}
        onOpenLab={(bookmark) => openBookmarkInLab(bookmark, "court3d")}
        onUpdateDescription={(bookmarkId, description) =>
          updateShotBookmarkDescription(packageId, bookmarkId, description)
        }
        onDelete={(bookmarkId) => deleteShotBookmark(packageId, bookmarkId)}
      />

      <Box
        ref={setShotSpaceOverlayPortalEl}
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 90,
          overflow: "visible",
          pointerEvents: "none",
        }}
      />

      <Box
        onMouseDown={(e) => {
          e.preventDefault();
          resizeRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            startWidth: frame.width,
            startHeight: frame.height,
          };
        }}
        sx={{
          position: "absolute",
          right: 0,
          bottom: 0,
          width: 18,
          height: 18,
          cursor: "nwse-resize",
          borderRight: "2px solid rgba(255,255,255,0.45)",
          borderBottom: "2px solid rgba(255,255,255,0.45)",
        }}
      />
    </Box>
  );
}
